import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import CustomerIntake from '../components/CustomerIntake';
import {
  Plus, X, Trash2, GripVertical, Check, Users,
  Clock, Edit2, RefreshCw, MapPin, UploadCloud,
  Map, AlertTriangle, Zap, List, ChevronLeft, ChevronRight, ChevronDown, Search, Droplets, Timer,
  MessageSquare, ArrowLeftRight, Play, Pause, Square,
  MoreHorizontal, CalendarClock,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { byRecentAssignment, fmtLastWorked } from '../utils/people';
import { MapContainer, TileLayer, Marker, Tooltip, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { businessToday, fmtClock, fmtTsTime, fmtWindow } from '../utils/time';
import CrewNoteEditor from '../components/CrewNoteEditor';
import { useReadOnly } from '../hooks/useReadOnly';
import RoutePlanPanel from '../components/RoutePlanPanel';
import AccountMark from '../components/AccountMark';

const SEASON_END = '2026-12-01';
const ROUTE_COLORS = ['#7c3aed', '#2563eb', '#16a34a', '#d97706', '#dc2626', '#0891b2'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmtDateShort(iso: string) {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateFull(iso: string) {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function todayStr() {
  return businessToday();
}

/**
 * The route's name with the day it runs next on the end — "Thursday (25th)".
 *
 * The owner, Sep 20 2026: "change the name of the route to say Thursday (25th)
 * so I know which Thursday is this week." Every Thursday route is called
 * Thursday, so on a busy page they are impossible to tell apart. A name that
 * already carries a date of its own ("Thursday Oct 1 — one-off") is left alone.
 */
function ordinalDay(iso: string) {
  const n = Number(String(iso).slice(8, 10));
  if (!n) return '';
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
  return `${n}${suffix}`;
}

function routeLabel(name: string, runsOn?: string | null) {
  const label = String(name || '');
  if (!runsOn || /\d/.test(label)) return label;
  const day = ordinalDay(runsOn);
  return day ? `${label} (${day})` : label;
}

// Central-time calendar day, plus the 2-month lookback floor for the date jump
const LOOKBACK_DAYS = 60;
function todayCT() {
  return businessToday();
}
function addDays(iso: string, n: number) {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function minJumpDate() { return addDays(todayCT(), -LOOKBACK_DAYS); }
/** Clamp a requested date into the lookback window. Returns [date, clamped]. */
function clampJumpDate(iso: string): [string, boolean] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return [todayCT(), false];
  const min = minJumpDate();
  return iso < min ? [min, true] : [iso, false];
}
/**
 * Loose date parsing for the search bar: 8/21/26, 8/21/2026, 2026-08-21,
 * Aug 21, Aug 21 2026, August 21. Two-digit years mean 20xx. Returns ISO or null.
 */
function parseLooseDate(text: string): string | null {
  const s = text.trim();
  if (!s) return null;
  const year = new Date().getFullYear();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return build(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?$/);
  if (m) return build(m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : year, +m[1], +m[2]);
  m = s.match(/^([a-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{2,4}))?$/i);
  if (m) {
    const mi = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'].indexOf(m[1].slice(0, 3).toLowerCase());
    if (mi >= 0) return build(m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : year, mi + 1, +m[2]);
  }
  return null;
  function build(y: number, mo: number, d: number): string | null {
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCMonth() !== mo - 1) return null;
    return dt.toISOString().slice(0, 10);
  }
}

/** Client twin of the server's pair-block ordering: partners stay adjacent. */
function normalizePairs(list: any[]): any[] {
  const out: any[] = [];
  const placed = new Set<number>();
  // plain object, not Map — `Map` is the lucide icon in this file's imports
  const byId: Record<number, any> = {};
  for (const s of list) byId[s.job_id] = s;
  for (const s of list) {
    if (placed.has(s.job_id)) continue;
    const partner = s.pair_partner_job_id != null ? byId[s.pair_partner_job_id] : null;
    if (!partner || placed.has(partner.job_id)) { out.push(s); placed.add(s.job_id); continue; }
    let first = s, second = partner;
    if (s.pair_is_first === false && partner.pair_is_first === true) { first = partner; second = s; }
    out.push(first, second); placed.add(first.job_id); placed.add(second.job_id);
  }
  return out;
}

/** Every service day a route runs inside the lookback window, past and future. */
function routeServiceDays(nextRun: string | null, intervalDays: number | null, defaultDays?: string | null): string[] {
  // A brand-new route has no jobs yet, so no next_run — offer the weekdays it
  // was created for (weekly) so its first stop lands on the right day.
  if (!nextRun) {
    const dows = String(defaultDays || '').split(',').map(s => Number(s.trim())).filter(n => Number.isInteger(n) && n >= 0 && n <= 6);
    if (!dows.length) return [];
    const out: string[] = [];
    let cur = todayStr();
    for (let i = 0; i < 120 && cur <= SEASON_END; i++) {
      if (dows.includes(new Date(cur + 'T12:00:00').getDay())) out.push(cur);
      cur = addDays(cur, 1);
    }
    return out;
  }
  const step = intervalDays || 7;
  const min = minJumpDate();
  const out: string[] = [];
  let cur = nextRun;
  while (cur >= min) { out.push(cur); cur = addDays(cur, -step); }
  const end = SEASON_END;
  cur = addDays(nextRun, step);
  while (cur <= end) { out.push(cur); cur = addDays(cur, step); }
  return out.sort();
}

const emptyForm = {
  name: '', notes: '',
  route_category: 'mowing', default_days: '', color: '#7c3aed', is_active: true,
  planned_start_time: '',
  leaves_home: true,
  start_address: '',
};

export default function RoutesPage() {
  const qc = useQueryClient();
  // Where the routes start (Settings → Service area), for "leaves CR 175 8:00" on the cards (services/homeBase).
  const { data: homeBaseInfo } = useQuery<any>('home-base', () => api.get('/settings/home-base').then(r => r.data), { staleTime: 60_000 });
  const baseName = String(homeBaseInfo?.label || 'home');
  const [createModal, setCreateModal] = useState(false);
  const [editRoute, setEditRoute] = useState<any | null>(null);
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const [sheetsMsg, setSheetsMsg] = useState<string | null>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [selectedDates, setSelectedDates] = useState<Record<number, string>>({});
  const [expandedRoutes, setExpandedRoutes] = useState<Record<number, boolean>>({});
  // While a searched date is showing, expansion is DERIVED: a route is open only
  // if it runs that day (dateOverrides holds taps made after the jump). No
  // effect ordering or stale state can open the other routes.
  const [dateOverrides, setDateOverrides] = useState<Record<number, boolean>>({});
  const [shiftPanelRoute, setShiftPanelRoute] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [showOneTime, setShowOneTime] = useState(false);
  const [showOneOff, setShowOneOff] = useState(false);
  const [search, setSearch] = useState('');

  // ── Date jump: /routes?date=YYYY-MM-DD, 60-day lookback ────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const viewDate = searchParams.get('date') || '';
  function jumpTo(iso: string | null) {
    if (!iso) { setSearchParams({}); return; }
    const [d, clamped] = clampJumpDate(iso);
    if (clamped) toast.info(`Lookback is 2 months — showing ${fmtDateFull(d)}`);
    setSearchParams({ date: d });
  }

  // Find which route a customer lives on — searches upcoming route stops
  const { data: searchData, isFetching: searching } = useQuery(
    ['route-stop-search', search.trim()],
    () => api.get('/routes/search-stops', { params: { q: search.trim() } }).then(r => r.data.results as any[]),
    { enabled: search.trim().length >= 2, keepPreviousData: true }
  );

  // Only the routes that regenerate. One-time work — treatments, bush jobs,
  // mulch, rock work — is real work, but it is not a route: it happens once and
  // it is done. It lives in Jobs now, and the strip at the bottom of this page
  // says where it went. The key stays prefixed 'routes' so every existing
  // invalidateQueries('routes') still refreshes it — react-query v3 matches
  // query keys by prefix.
  const { data: routesData, isLoading } = useQuery(
    ['routes', 'recurring'],
    // One-day routes (Route loose jobs) are real routes too: crew, clock, drive time, pay.
    () => api.get('/routes', { params: { kind: 'recurring,empty,day' } }).then(r => r.data.routes as any[])
  );
  const { data: oneTimeData } = useQuery(
    ['routes', 'onetime'],
    () => api.get('/routes', { params: { kind: 'onetime' } }).then(r => r.data.routes as any[])
  );
  const { data: employeesData } = useQuery('employees', () =>
    api.get('/employees').then(r => r.data.employees as any[])
  );

  useEffect(() => {
    if (!routesData) return;
    setSelectedDates(prev => {
      const next = { ...prev };
      const today = todayStr();
      for (const r of routesData) {
        if (!next[r.id] && r.next_run) {
          // A one-day route opens on its one day.
          if (r.kind === 'day') { next[r.id] = String(r.next_run).slice(0, 10); continue; }
          // Default to the first chip on or after today (not the past anchor).
          // next_run may be a completed past date used as the chip-generation anchor;
          // we still want the "current" view to land on today-or-later.
          const dates = routeServiceDays(r.next_run, Number(r.interval_days) || null);
          next[r.id] = dates.find(d => d >= today) || dates[dates.length - 1] || r.next_run;
        }
      }
      return next;
    });
    setExpandedRoutes(prev => {
      const next = { ...prev };
      for (const r of routesData) {
        if (next[r.id] === undefined) next[r.id] = false;
      }
      return next;
    });
  }, [routesData]);

  // A jumped-to date opens ONLY the routes that run that day (the server's
  // date mode lists routes with live stops on the date) and collapses the rest.
  // Searching 9/8 opens Tuesday Week B, not every route.
  const { data: routesOnDate } = useQuery(
    ['routes-on-date', viewDate],
    () => api.get('/routes', { params: { date: viewDate } }).then(r => (r.data.routes as any[]).map((x: any) => Number(x.id))),
    { enabled: !!viewDate }
  );
  useEffect(() => { setDateOverrides({}); }, [viewDate]);
  useEffect(() => {
    if (!viewDate || !routesData || !routesOnDate) return;
    const on = new Set(routesOnDate);
    setSelectedDates(prev => {
      const next = { ...prev };
      for (const r of routesData) if (on.has(Number(r.id))) next[r.id] = viewDate;
      return next;
    });
    // Mirror into the normal state too, so clearing the date leaves things sane.
    setExpandedRoutes(prev => {
      const next = { ...prev };
      for (const r of routesData) next[r.id] = on.has(Number(r.id));
      return next;
    });
    // Bring the open route into view so the phone lands on it, not on the search box.
    const first = routesData.find(r => on.has(Number(r.id)));
    if (first) setTimeout(() => document.getElementById(`route-card-${first.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [viewDate, routesData, routesOnDate]);
  function routeIsExpanded(routeId: number) {
    if (viewDate) {
      if (dateOverrides[routeId] !== undefined) return dateOverrides[routeId];
      return !!routesOnDate && routesOnDate.includes(Number(routeId));
    }
    return expandedRoutes[routeId] !== false;
  }
  function toggleRoute(routeId: number, open: boolean) {
    if (viewDate) setDateOverrides(prev => ({ ...prev, [routeId]: open }));
    setExpandedRoutes(prev => ({ ...prev, [routeId]: open }));
  }

  // Typing a date is enough — no Enter needed. "9/8" jumps as soon as you pause.
  useEffect(() => {
    const d = parseLooseDate(search);
    if (!d || d === viewDate) return;
    const t = setTimeout(() => jumpTo(d), 450);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const saveRoute = useMutation(async () => {
    if (!form.name.trim()) throw new Error('Name required');
    const payload = {
      name: form.name, notes: form.notes,
      route_type: form.route_category,
      default_days: form.default_days || null,
      color: form.color,
      is_active: form.is_active ? 1 : 0,
      planned_start_time: form.planned_start_time || null,
      start_location: form.leaves_home ? 'home' : null,
      // Where the crew's map draws the Start pin (blank = home base). The server looks the address up once.
      start_address: String(form.start_address || '').trim() || null,
    };
    if (editRoute) await api.put(`/routes/${editRoute.id}`, payload);
    else await api.post('/routes', payload);
  }, {
    onSuccess: () => {
      qc.invalidateQueries('routes');
      toast.success(editRoute ? 'Route updated' : 'Route created');
      setCreateModal(false); setEditRoute(null); setForm(emptyForm);
    },
    onError: (e: any) => { toast.error(e.message || 'Failed'); },
  });

  const removeJob = useMutation(async ({ routeId, jobId, scope }: { routeId: number; jobId: number; scope?: string }) => {
    await api.delete(`/routes/${routeId}/jobs/${jobId}`, { params: { scope: scope || 'skip' } });
  }, {
    onSuccess: (_, vars) => {
      qc.invalidateQueries(['route-stops', vars.routeId]);
      qc.invalidateQueries('routes');
      toast.success(vars.scope === 'future' ? 'Stop removed going forward' : 'Stop skipped for this date');
    },
    onError: () => { toast.error('Failed to remove'); },
  });

  // Closest work first (owner, Oct 14 2026: "order the route tab in closest date first so today is the top of
  // the list and then each day out from there"). The key is the server's next_work_day — the first day on or
  // after today with any live stop on the route, finished stops included, so today's route stays on top while
  // it is being worked — else the first projected cadence date, else last. Ties keep the server's weekday order.
  const today = todayStr();
  const nextDayOf = (r: any): string | null => {
    if (r.next_work_day) return String(r.next_work_day).slice(0, 10);
    const oneDay = (r.kind === 'day' || r.kind === 'onetime') && !Number(r.interval_days);
    const dates: string[] = oneDay
      ? [r.next_run || r.route_date].filter(Boolean).map((d: string) => String(d).slice(0, 10))
      : routeServiceDays(r.next_run, Number(r.interval_days) || null, r.default_days);
    return dates.find(d => d >= today) || null;
  };
  const dated = (routesData || []).map((r: any, i: number) => ({ r, i, day: nextDayOf(r) }));
  // "Make a collapsed thing for one off routes": a one-day route whose day has passed folds into a group below
  // the list. One with work today or ahead is work in hand and stays in the dated list; so does any route that
  // runs the searched date, or the jump could not scroll to it.
  const onDate = new Set<number>(viewDate && routesOnDate ? routesOnDate : []);
  const isPastOneOff = (x: { r: any; day: string | null }) => x.r.kind === 'day' && !x.day && !onDate.has(Number(x.r.id));
  const routes = dated.filter(x => !isPastOneOff(x))
    .sort((a, b) => (a.day && b.day ? a.day.localeCompare(b.day) : a.day ? -1 : b.day ? 1 : 0) || a.i - b.i)
    .map(x => x.r);
  const oneOffRoutes = dated.filter(isPastOneOff)
    .sort((a, b) => String(b.r.next_run || b.r.route_date || '').localeCompare(String(a.r.next_run || a.r.route_date || '')))
    .map(x => x.r);
  const oneOffDays = oneOffRoutes.map((r: any) => fmtDateShort(String(r.next_run || r.route_date || '').slice(0, 10))).join(', ');
  const oneTimeRoutes = oneTimeData || [];
  const employees = employeesData || [];

  // One route card, drawn the same in the dated list and in the one-off group below it.
  const renderRouteCard = (route: any) => {
    // A one-day route runs on its one day — never a made-up weekly list of dates.
    const upcomingDates = (route.kind === 'day' || route.kind === 'onetime') && !Number(route.interval_days)
      ? [route.next_run || route.route_date].filter(Boolean).map((d: string) => String(d).slice(0, 10))
      : routeServiceDays(route.next_run, Number(route.interval_days) || null, route.default_days);
    const selDate = selectedDates[route.id] || upcomingDates[0] || '';
    const totalHours = (route.total_planned_minutes || 0) / 60;
    const isExpanded = routeIsExpanded(Number(route.id));

    return (
      <div key={route.id} id={`route-card-${route.id}`} className="card overflow-hidden">
        {/* Route header */}
        <div
          className="flex items-center px-3 sm:px-4 py-3 gap-2 sm:gap-3 border-b border-gray-100 cursor-pointer select-none"
          onClick={() => toggleRoute(Number(route.id), !isExpanded)}
        >
          <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
          <div className="w-1.5 sm:w-2 h-12 rounded-full flex-shrink-0"
            style={{ backgroundColor: route.color || '#7c3aed' }} />
          <div className="flex-1 min-w-0">
            {/* Name on its own full-width line so it never word-stacks on phones.
                Tapping it expands the route (the whole header toggles); the pencil renames. */}
            {/* The date on the name is the next day the route is really
                booked on (run_days, straight off its stops) — not a date
                worked forward from an interval, which drifts when the
                interval is off (owner, Sep 21 2026: "the names for the
                routes were supposed to change based on the next closest
                date for the stops"). */}
            <p className="text-sm font-semibold text-gray-900 leading-snug">{routeLabel(route.name, (route.run_days || [])[0] || upcomingDates[0])}</p>
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
              <span className="text-[11px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">#{route.id}</span>
              {route.is_active === 0 && (
                <span className="text-[11px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Inactive</span>
              )}
              <span className="text-[11px] bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded">
                {route.frequency_label || 'Recurring'}
              </span>
              <span className="hidden sm:inline text-[11px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded capitalize">
                {route.route_type === 'fertilizing' ? 'Fertilizing' : 'Mowing'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {route.stop_count || 0} stops
              {totalHours > 0 && ` · ~${totalHours.toFixed(1)}h`}
              {route.kind === 'day' && route.next_run && ` · ${fmtDateFull(String(route.next_run).slice(0, 10))}`}
              {route.planned_start_time && (String(route.start_location || '').toLowerCase() === 'home'
                ? ` · leaves ${baseName} ${route.planned_start_time}`
                : ` · starts ${route.planned_start_time}`)}
            </p>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button
              title="Reschedule route"
              onClick={() => setShiftPanelRoute(shiftPanelRoute === route.id ? null : route.id)}
              className={`p-1 sm:p-1.5 rounded ${shiftPanelRoute === route.id ? 'text-amber-600 bg-amber-50' : 'text-gray-500 hover:text-amber-600'}`}>
              <ArrowLeftRight className="h-4 w-4" />
            </button>
            <button onClick={() => openEdit(route)}
              className="p-1 sm:p-1.5 text-gray-500 hover:text-brand-600 rounded">
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              title="Delete route"
              onClick={() => setDeleteTarget(route)}
              className="p-1 sm:p-1.5 text-gray-500 hover:text-red-500 rounded">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* A past day of this route with stops nobody got to — a rain-out — offers them onto today in one tap
            (owner, Oct 30 2026: "move all the houses from yesterday to today's route"). Server: GET /routes `leftover`. */}
        {route.leftover?.count > 0 && (
          <LeftoverStrip routeId={route.id} leftover={route.leftover}
            onShow={() => { setSelectedDates(prev => ({ ...prev, [route.id]: route.leftover.date })); toggleRoute(Number(route.id), true); }}
            onDone={() => { qc.invalidateQueries('routes'); qc.invalidateQueries('route-stops'); qc.invalidateQueries('calendar-jobs'); }} />
        )}

        {/* Expandable content */}
        {/* The ⇄ panel opens whether or not the card is expanded — tapping it on a closed card used to do nothing. */}
        {shiftPanelRoute === route.id && (
          <ReschedulePanel
            routeId={route.id}
            routeName={route.name}
            fromDate={selDate || upcomingDates[0] || todayStr()}
            onDone={() => { setShiftPanelRoute(null); qc.invalidateQueries('routes'); }}
          />
        )}
        {isExpanded && (
          <>
            {/* Date chips live INSIDE the expanded route (owner asked for them back
                here, Sep 7); the page-level date bar stays gone — search jumps days. */}
            {upcomingDates.length > 0 && (
              <DateChipRow
                dates={upcomingDates}
                selected={selDate}
                color={route.color}
                onSelect={d => setSelectedDates(prev => ({ ...prev, [route.id]: d }))}
              />
            )}

            {selDate ? (
              <div>
                <AssignmentPanel
                  routeId={route.id}
                  selectedDate={selDate}
                  employees={employees}
                  onSaved={() => qc.invalidateQueries('routes')}
                />
                <div className="px-4 py-3">
                  <RouteStopList
                    routeId={route.id}
                    date={selDate}
                    routeColor={route.color}
                    routeIntervalDays={Number(route.interval_days) || 14}
                    onRemove={(jobId, scope) => removeJob.mutate({ routeId: route.id, jobId, scope })}
                  />
                </div>
              </div>
            ) : (
              // No date context at all — show template stops
              <div className="px-4 py-3">
                <RouteStopList
                  routeId={route.id}
                  date=""
                  routeColor={route.color}
                  routeIntervalDays={Number(route.interval_days) || 14}
                  onRemove={(jobId, scope) => removeJob.mutate({ routeId: route.id, jobId, scope })}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  function openCreate() { setForm(emptyForm); setCreateModal(true); }
  function openEdit(r: any) {
    setForm({
      name: r.name || '', notes: r.notes || '',
      route_category: r.route_type || 'mowing',
      default_days: r.default_days || '',
      color: r.color || '#7c3aed',
      is_active: r.is_active !== 0,
      planned_start_time: r.planned_start_time || '',
      leaves_home: String(r.start_location || 'home').toLowerCase() === 'home',
      start_address: r.start_address || '',
      // What is saved for the start pin, so the form can say when the address was not found on the map.
      _saved_start: { address: r.start_address || '', pinned: r.start_lat != null },
    });
    setEditRoute(r);
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Routes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Recurring mowing routes through Dec 1, 2026
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              setSheetsExporting(true); setSheetsMsg(null);
              try {
                const res = await api.post('/integrations/sheets/export/jobs/completed');
                setSheetsMsg(res.data.message);
              } catch (err: any) {
                setSheetsMsg(err.response?.data?.message || 'Export failed');
              } finally { setSheetsExporting(false); }
            }}
            disabled={sheetsExporting}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-1.5 bg-white disabled:opacity-50"
          >
            <UploadCloud className="h-4 w-4" /> {sheetsExporting ? 'Exporting…' : 'Completed → Sheets'}
          </button>
          <button onClick={openCreate} className="btn-primary">
            <Plus className="h-4 w-4 mr-1" /> New Route
          </button>
        </div>
      </div>

      {sheetsMsg && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
          {sheetsMsg}
        </div>
      )}

      {/* ── Find a customer's route ── */}
      <div className="relative">
        <div className="relative">
          <Search className="h-4 w-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const d = parseLooseDate(search);
                if (d) { jumpTo(d); setSearch(''); }
              }
            }}
            placeholder="Search a customer, address — or type a date like 8/21 or Aug 21 to jump there"
            className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {search.trim().length >= 2 && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
            {parseLooseDate(search) && (
              <button
                onClick={() => { const d = parseLooseDate(search)!; jumpTo(d); setSearch(''); }}
                className="w-full text-left px-4 py-2.5 bg-brand-50 hover:bg-brand-100 border-b border-brand-100 text-sm font-semibold text-brand-800"
              >
                📅 {viewDate === parseLooseDate(search) ? 'Showing' : 'Jump to'} {fmtDateFull(parseLooseDate(search)!)} <span className="text-xs font-normal text-brand-600">{viewDate === parseLooseDate(search) ? '— only the routes that run that day are open below' : '— press Enter'}</span>
              </button>
            )}
            {(searchData || []).length === 0 && !parseLooseDate(search) ? (
              <p className="px-4 py-3 text-sm text-gray-500">{searching ? 'Searching…' : 'No upcoming route stops match.'}</p>
            ) : parseLooseDate(search) && (searchData || []).length === 0 ? (
              null
            ) : (searchData || []).map((r: any) => (
              <button
                key={`${r.job_id}`}
                onClick={() => {
                  toggleRoute(Number(r.route_id), true);
                  setSelectedDates(prev => ({ ...prev, [r.route_id]: r.next_date }));
                  setSearch('');
                  setTimeout(() => document.getElementById(`route-card-${r.route_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                }}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 break-words">{r.customer_name}<AccountMark mark={r.account_mark} /></p>
                  <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full text-snow"
                    style={{ backgroundColor: r.route_color || '#7c3aed' }}>
                    {r.route_name}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {r.property_label || r.address}{r.city ? `, ${r.city}` : ''}
                  <span className="text-brand-600 font-medium"> · next {fmtDateShort(r.next_date)}</span>
                  {r.frequency ? ` · ${freqLabel(r.frequency, r.custom_interval_days)}` : ''}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* After a date search: where you are, and two tiny ways back. No strip, no scroller. */}
      {viewDate && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-gray-800">Viewing {fmtDateFull(viewDate)}{viewDate < todayCT() ? ' · past day' : ''}</span>
          {routesOnDate && (
            <span className="text-xs text-gray-500">
              {routesOnDate.length === 0 ? 'no route runs that day' : `${routesOnDate.length} route${routesOnDate.length === 1 ? '' : 's'} that day`}
            </span>
          )}
          {viewDate !== todayCT() && (
            <button onClick={() => jumpTo(todayCT())} className="text-xs px-2.5 py-1 rounded-full border bg-white text-gray-600 border-gray-200 hover:border-brand-400">Today</button>
          )}
          <button onClick={() => jumpTo(null)} className="text-xs text-gray-500 flex items-center gap-1"><X className="h-3 w-3" /> Clear</button>
        </div>
      )}

      <OddDays />
      <PasteRoute />
      <RoutePlanPanel />

      {isLoading ? (
        <div className="text-center text-gray-500 py-8">Loading…</div>
      ) : routes.length === 0 && oneOffRoutes.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>No routes yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {routes.map(renderRouteCard)}
        </div>
      )}

      {/* One-off routes whose day has passed (owner, Oct 14 2026: "make a collapsed thing for one off routes").
          Listed, never hidden: the header counts them and names their days; open it and they are whole route
          cards — crew, clock, drive and pay — the same as above. */}
      {oneOffRoutes.length > 0 && (
        <div className="card">
          <button
            onClick={() => setShowOneOff(v => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
          >
            <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${showOneOff ? '' : '-rotate-90'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700">
                {oneOffRoutes.length} one-off {oneOffRoutes.length === 1 ? 'route' : 'routes'} · {oneOffDays}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Days built as a route and already run. Crew, clock, drive and pay are all still here.
              </p>
            </div>
          </button>
          {showOneOff && (
            <div className="space-y-4 p-3 border-t border-gray-100">
              {oneOffRoutes.map(renderRouteCard)}
            </div>
          )}
        </div>
      )}

      {/* One-time work that used to sit up there with the mowing routes.
          Listed, not hidden — you should always be able to find it again. */}
      {oneTimeRoutes.length > 0 && (
        <div className="card">
          <button
            onClick={() => setShowOneTime(v => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
          >
            <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${showOneTime ? '' : '-rotate-90'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700">
                {oneTimeRoutes.length} one-time {oneTimeRoutes.length === 1 ? 'job' : 'jobs'} moved to Jobs
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Treatments, bush work, mulch — these happen once, so they are not routes.
                Nothing was deleted.
              </p>
            </div>
            <Link
              to="/jobs"
              onClick={e => e.stopPropagation()}
              className="text-sm font-medium text-brand-600 hover:text-brand-700 flex-shrink-0"
            >
              Open Jobs →
            </Link>
          </button>

          {showOneTime && (
            <ul className="divide-y divide-gray-50 border-t border-gray-100">
              {oneTimeRoutes.map((r: any) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="w-1.5 h-7 rounded-full flex-shrink-0"
                    style={{ backgroundColor: r.color || '#9ca3af' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">{r.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {r.job_count_total} job{r.job_count_total === 1 ? '' : 's'}
                      {r.next_run ? ` · ${fmtDateFull(r.next_run)}` : ''}
                    </p>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono flex-shrink-0">#{r.id}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {(createModal || editRoute) && (
        <RouteFormModal
          form={form} setForm={setForm}
          onClose={() => { setCreateModal(false); setEditRoute(null); }}
          onSave={() => saveRoute.mutate()}
          saving={saveRoute.isLoading}
          title={editRoute ? 'Edit Route' : 'New Route'}
        />
      )}

      {deleteTarget && (
        <DeleteRouteModal
          route={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={msg => {
            setDeleteTarget(null);
            // The other two route lists use their own keys, so a plain
            // invalidateQueries('routes') never reaches them and they keep
            // showing a route that no longer exists.
            qc.invalidateQueries('routes');
            qc.invalidateQueries('routes-list-jobs');
            qc.invalidateQueries('routes-for-calendar');
            qc.invalidateQueries('jobs-list');
            toast.success(msg);
          }}
        />
      )}
    </div>
  );
}

// ── Delete a route ────────────────────────────────────────────────────────────
//
// The server refuses with 409 ROUTE_HAS_WORK when the route still has live jobs
// ahead of it, and sends back an explanation. The old window.confirm threw that
// explanation away and said "Failed to delete", which is why deleting a route
// looked broken. Show what the server actually said, then let it be overridden
// on purpose.
function DeleteRouteModal({ route, onClose, onDeleted }: {
  route: any; onClose: () => void; onDeleted: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<any | null>(null);

  async function run(force: boolean) {
    setBusy(true);
    try {
      const { data } = await api.delete(`/routes/${route.id}`, {
        params: force ? { force: 'true' } : {},
      });
      onDeleted(data?.message || `Deleted "${route.name}"`);
    } catch (err: any) {
      const body = err?.response?.data;
      if (err?.response?.status === 409 && body?.code === 'ROUTE_HAS_WORK') {
        setBlocked(body);
      } else {
        toast.error(body?.message || 'Failed to delete route');
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Delete “{route.name}”</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {blocked ? (
            <>
              <div className="flex gap-2.5 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">{blocked.message}</p>
              </div>
              <p className="text-sm text-gray-600">
                Every job is kept either way — they just stop belonging to a route and
                show up under Jobs. What is lost is this route’s own history and its
                stop order.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-600">
              The jobs on this route are kept. They become unrouted and move to the
              Jobs tab. The route’s stop order and run history are deleted.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => run(!!blocked)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {busy ? 'Deleting…' : blocked ? 'Delete anyway' : 'Delete route'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Horizontal date chip row (inside an expanded route) ───────────────────────
function DateChipRow({ dates, selected, color, onSelect }: {
  dates: string[]; selected: string; color?: string; onSelect: (d: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const today = todayStr();

  useEffect(() => {
    const el = scrollRef.current?.querySelector('[data-selected="true"]') as HTMLElement | null;
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [selected]);

  function scroll(dir: 'left' | 'right') {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  }

  return (
    <div className="flex items-center border-b border-gray-100 bg-gray-50/60 px-2 py-2 gap-1">
      <button onClick={() => scroll('left')} className="p-1 text-gray-500 hover:text-gray-600 flex-shrink-0 rounded">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div ref={scrollRef} className="flex gap-1.5 overflow-x-auto flex-1 py-0.5 px-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' } as any}>
        {dates.map(d => {
          const isSel   = d === selected;
          const isPast  = d < today;
          const isToday = d === today;
          return (
            <button key={d} data-selected={isSel} onClick={() => onSelect(d)}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${
                isSel    ? 'text-snow border-transparent' :
                isPast   ? 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200' :
                isToday  ? 'bg-white text-gray-700 border-gray-400 hover:border-gray-600' :
                           'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
              style={isSel ? { backgroundColor: color || '#7c3aed', borderColor: color || '#7c3aed' } : {}}>
              {fmtDateShort(d)}
              {isToday && !isSel && <span className="ml-1 text-[10px] text-gray-500">today</span>}
            </button>
          );
        })}
      </div>
      <button onClick={() => scroll('right')} className="p-1 text-gray-500 hover:text-gray-600 flex-shrink-0 rounded">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Numbered map marker ───────────────────────────────────────────────────────
function makeNumberedIcon(n: number, color = '#7c3aed') {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35)">${n}</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14], tooltipAnchor: [14, 0],
  });
}

// ── Flexible reschedule panel ─────────────────────────────────────────────────
// ── The day-in-use warning ────────────────────────────────────────────────────
// The crew is running this route right now; the server refused (or kept the
// crew's order) and said it can be forced. Owner, Oct 19 2026: "just give a
// popup saying warning and allow me to do anyway or go back."
function DayInUseModal({ message, onAnyway, onBack }: { message: string; onAnyway: () => void; onBack: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onBack}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-gray-900 text-base">⚠️ The crew is running this route right now</h3>
        <p className="text-sm text-gray-700">{message}</p>
        <p className="text-sm text-gray-600">
          Changing it now reshuffles their phones mid-route. They pick up the new order within about 15 seconds,
          from whatever stop they are on.
        </p>
        <div className="flex gap-2 pt-1">
          <button onClick={onBack} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium">Go back</button>
          <button onClick={onAnyway} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-white font-semibold">Do it anyway</button>
        </div>
      </div>
    </div>
  );
}

function ReschedulePanel({ routeId, routeName, fromDate, onDone }: {
  routeId: number;
  routeName: string;
  fromDate: string;
  onDone: () => void;
}) {
  const [toDate, setToDate] = useState('');
  // A rain day is the common move, so a single date is the default — changing the weekday for good is the deliberate choice.
  const [scope, setScope] = useState<'remaining' | 'this_only' | 'this_and_future' | 'future_pending'>('this_only');
  const [busy, setBusy] = useState(false);

  const deltaDays = toDate
    ? Math.round((new Date(toDate + 'T12:00:00').getTime() - new Date(fromDate + 'T12:00:00').getTime()) / 86400000)
    : 0;

  const previewLabel = toDate
    ? (deltaDays === 0
        ? 'No change — same date'
        : `${deltaDays > 0 ? '+' : ''}${deltaDays} days: ${fmtDateFull(fromDate)} → ${fmtDateFull(toDate)}`)
    : '';

  const scopeLabels: Record<string, string> = {
    remaining: 'Only the stops not started yet — same times (works while the day is running)',
    this_only: 'This date only',
    this_and_future: 'This date + all future',
    future_pending: 'Future dates only (skip this date)',
  };

  // Rain day: one tap moves just this date, with Undo on the toast (owner, Oct 1 2026: "make them faster").
  const plusDays = (iso: string, n: number) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  const nextWeekday = (iso: string) => { let d = plusDays(iso, 1); while ([0, 6].includes(new Date(`${d}T12:00:00`).getDay())) d = plusDays(d, 1); return d; };
  // The server refused because the crew is on this day and said it can be forced: the warning, then the same call
  // again with force when he says "do it anyway".
  const [inUse, setInUse] = useState<{ message: string; retry: () => void } | null>(null);
  const inUseRefusal = (e: any) => e?.response?.status === 409 && e.response?.data?.code === 'ROUTE_IN_USE' && e.response?.data?.can_force;
  async function quickMove(to: string, force = false) {
    setBusy(true);
    try {
      const { data } = await api.post(`/routes/${routeId}/reschedule`, { from_date: fromDate, to_date: to, scope: 'this_only', ...(force ? { force: true } : {}) });
      const undo = async () => {
        try {
          await api.post(`/routes/${routeId}/reschedule`, { from_date: to, to_date: fromDate, scope: 'this_only' });
          toast.success(`${routeName} is back on ${fmtDateFull(fromDate)}`);
          onDone();
        } catch (e: any) { toast.error(e.response?.data?.message || 'Could not undo'); }
      };
      toast.success(
        <span>{data.message || `Moved to ${fmtDateFull(to)}`}{' '}
          <button onClick={undo} className="underline font-bold ml-1">Undo</button></span>,
        { autoClose: 10000 });
      onDone();
    } catch (e: any) {
      if (inUseRefusal(e)) setInUse({ message: e.response.data.message, retry: () => quickMove(to, true) });
      else toast.error(e.response?.data?.message || 'Reschedule failed');
    } finally {
      setBusy(false);
    }
  }

  async function doReschedule(force = false) {
    if (!toDate) { toast.error('Choose a target date'); return; }
    if (deltaDays === 0) { toast.error('Target date is the same as the current date'); return; }
    if (scope === 'this_only') return quickMove(toDate, force);
    if (!force && !window.confirm(
      `Reschedule "${routeName}"\n${scopeLabels[scope]}\n\n${previewLabel}\n\nPending jobs only — cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/routes/${routeId}/reschedule`, { from_date: fromDate, to_date: toDate, scope, ...(force ? { force: true } : {}) });
      toast.success(data.message || `Moved ${data.jobs_shifted} job(s)`);
      onDone();
    } catch (e: any) {
      if (inUseRefusal(e)) setInUse({ message: e.response.data.message, retry: () => doReschedule(true) });
      else toast.error(e.response?.data?.message || 'Reschedule failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-4 mb-3 border border-amber-200 rounded-xl bg-amber-50 p-3 space-y-3">
      {inUse && (
        <DayInUseModal message={inUse.message}
          onAnyway={() => { const r = inUse.retry; setInUse(null); r(); }}
          onBack={() => setInUse(null)} />
      )}
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="h-3.5 w-3.5 text-amber-600" />
        <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Reschedule Route</span>
        <span className="text-xs text-amber-600 ml-1">— from {fmtDateFull(fromDate)}</span>
      </div>

      {/* Rain day — one tap, this date only */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => quickMove(plusDays(fromDate, 1))} disabled={busy}
          className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold disabled:opacity-40">
          Tomorrow · {fmtDateFull(plusDays(fromDate, 1))}
        </button>
        {nextWeekday(fromDate) !== plusDays(fromDate, 1) && (
          <button onClick={() => quickMove(nextWeekday(fromDate))} disabled={busy}
            className="px-3 py-2 rounded-lg bg-white border border-amber-400 text-amber-800 text-sm font-bold disabled:opacity-40">
            Next weekday · {fmtDateFull(nextWeekday(fromDate))}
          </button>
        )}
      </div>
      <p className="text-[11px] text-amber-700 -mt-1">Moves only {fmtDateFull(fromDate)}'s stops that aren't done — the schedule stays the same. Or pick a date:</p>

      {/* Target date picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs font-medium text-amber-800 whitespace-nowrap">Move to:</label>
        <input
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          className="text-xs border border-amber-300 rounded-md px-2 py-1 bg-white text-gray-800 focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
        {previewLabel && (
          <span className={`text-xs font-medium ${deltaDays === 0 ? 'text-gray-500' : 'text-amber-700'}`}>
            {previewLabel}
          </span>
        )}
      </div>

      {/* Scope selection */}
      <div className="flex flex-wrap gap-4">
        {Object.entries(scopeLabels).map(([k, label]) => (
          <label key={k} className="flex items-center gap-1.5 text-xs text-amber-800 cursor-pointer">
            <input
              type="radio"
              name={`rscope-${routeId}`}
              checked={scope === k}
              onChange={() => setScope(k as typeof scope)}
              className="accent-amber-600"
            />
            {label}
          </label>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => doReschedule()}
          disabled={busy || !toDate || deltaDays === 0}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 font-medium"
        >
          {busy ? 'Moving…' : 'Apply'}
        </button>
        <p className="text-[10px] text-amber-600">
          Pending jobs only — completed and skipped work stays put. Crew assignments move too.
          {scope !== 'this_only'
            ? ' Changing the weekday also retargets this route’s recurring schedules, so future jobs generate on the new day.'
            : ' A single-date move does not change the recurring schedule.'}
          {scope === 'this_only' ? ' You get an Undo.' : ' Cannot be undone.'}
        </p>
      </div>
    </div>
  );
}

// ── Route map ─────────────────────────────────────────────────────────────────
function RouteMap({ jobs, color, start }: { jobs: any[]; color?: string; start?: { lat: number; lng: number; label: string } | null }) {
  const withCoords = jobs.filter(j => j.lat != null && j.lng != null);
  // Where the day starts (the route's own start address, else home base) — the same pin the crew's map draws.
  const startPt: [number, number] | null = start && Number.isFinite(Number(start.lat)) && Number.isFinite(Number(start.lng)) ? [Number(start.lat), Number(start.lng)] : null;
  if (withCoords.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-gray-500 py-8 text-sm text-center">
        <MapPin className="h-6 w-6 mb-2 text-gray-300" />
        <p className="font-medium">No GPS coordinates</p>
        <p className="text-xs text-gray-500 mt-1">
          Add lat/lng to properties to enable map view
        </p>
      </div>
    );
  }
  const center: [number, number] = [
    withCoords.reduce((s, j) => s + j.lat, 0) / withCoords.length,
    withCoords.reduce((s, j) => s + j.lng, 0) / withCoords.length,
  ];
  return (
    <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 320 }}>
      <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {(withCoords.length + (startPt ? 1 : 0)) > 1 && (
          <Polyline positions={[...(startPt ? [startPt] : []), ...withCoords.map(j => [j.lat, j.lng] as [number, number])]}
            color={color || '#7c3aed'} weight={2} opacity={0.6} dashArray="6,4" />
        )}
        {startPt && (
          <Marker position={startPt} icon={L.divIcon({ className: '', html: '<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:8px;background:#15803d;color:#fff;font-size:14px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)">\u2691</div>', iconSize: [28, 28], iconAnchor: [14, 14] })}>
            <Tooltip direction="top"><span className="text-xs font-medium">Start — {start?.label}</span></Tooltip>
          </Marker>
        )}
        {withCoords.map(j => (
          <Marker key={j.job_id || j.id} position={[j.lat, j.lng]}
            icon={makeNumberedIcon(jobs.indexOf(j) + 1, color || '#7c3aed')}>
            <Tooltip direction="top">
              <span className="text-xs font-medium">{j.customer_name}</span>
              {(j.property_label || j.address) && (
                <><br /><span className="text-xs text-gray-500">{j.property_label || j.address}</span></>
              )}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

// ── Nearest-neighbor reorder — what "Optimize" actually does ─────────────────
//
// Greedy nearest-neighbour on straight-line distance. Start at whichever stop is
// currently first, then repeatedly jump to the closest stop not yet used.
//
// Four things it is NOT, all of which matter on a real route:
//
//   · Not driving distance. No roads, no one-ways, no river, no I-35. Two stops
//     a thousand feet apart with no crossing between them look adjacent.
//   · Not optimal. Greedy nearest-neighbour typically comes out 15–25% longer
//     than the best possible order, and it characteristically strands one far
//     stop for last because nothing was ever near it.
//   · Not anchored. It starts wherever the list already starts, not at the shop
//     or at home. Reordering the list by hand first changes the answer.
//   · Not aware of stops without GPS. Those are appended at the end in their
//     existing order, wherever they physically are. The customer audit lists
//     which properties are missing coordinates.
//
// The distance is squared degrees — dLat² + dLng² — which treats a degree of
// longitude as equal to a degree of latitude. At Austin's latitude a degree of
// longitude is about 14% shorter on the ground, so east-west gaps are scored
// too heavily and the result leans toward north-south chains. Fine for ordering
// a dozen stops in one part of town; not a substitute for looking at the map.
//
// It also only reorders. Arrival times are stale until Recalc Times runs.
function nearestNeighborOrder(jobs: any[]): any[] {
  const withCoords = jobs.filter(j => j.lat != null && j.lng != null);
  const noCoords   = jobs.filter(j => j.lat == null || j.lng == null);
  if (withCoords.length < 2) return jobs;
  const dist = (a: any, b: any) => {
    const dLat = a.lat - b.lat, dLng = a.lng - b.lng;
    return dLat * dLat + dLng * dLng;
  };
  const remaining = [...withCoords];
  const ordered: any[] = [remaining.shift()!];
  while (remaining.length > 0) {
    const last = ordered[ordered.length - 1];
    let bestIdx = 0, bestDist = dist(last, remaining[0]);
    for (let i = 1; i < remaining.length; i++) {
      const d = dist(last, remaining[i]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    ordered.push(remaining.splice(bestIdx, 1)[0]);
  }
  return [...ordered, ...noCoords];
}

// ── Who actually worked these stops ───────────────────────────────────────────
//
// The route assignment says who was *sent* out that day, and until now that was
// also who got paid for every stop — so a crew member who skipped four of them
// was still paid for all twenty-two, and a stop worked alone still billed two
// man-hours per clock hour.
//
// This says who actually did the work. Pay, the My/Employee hours split and
// man-hours all follow it. Everyone on the team is listed, not just the people
// on that route, because the whole point is that the day did not go to plan.
function CrewPickerModal({ count, routeCrew, saving, onClose, onSave }: {
  count: number;
  routeCrew: { id: number; name: string }[];
  saving: boolean;
  onClose: () => void;
  onSave: (crewIds: number[]) => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const { data: employeesData } = useQuery('employees', () =>
    api.get('/employees').then(r => r.data.employees as any[])
  );
  const everyone: any[] = employeesData || [];
  const onRoute = new Set(routeCrew.map(c => c.id));

  // Default to the crew that was sent — the common edit is removing one person
  // from a few stops, not building the list up from nothing.
  const [picked, setPicked] = useState<Set<number>>(new Set(routeCrew.map(c => c.id)));

  function toggle(id: number) {
    setPicked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Who was sent first, then most-recently-assigned first, then by name.
  const sorted = [...everyone].sort((a, b) => {
    const ra = onRoute.has(a.id) ? 0 : 1, rb = onRoute.has(b.id) ? 0 : 1;
    return ra - rb || byRecentAssignment(a, b);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">
            Who worked {count === 1 ? 'this stop' : `these ${count} stops`}?
          </h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        <div className="px-5 py-3 overflow-y-auto flex-1 space-y-1">
          {sorted.length === 0 && <p className="text-sm text-gray-500 py-4">No crew members found.</p>}
          {sorted.map(emp => (
            <label key={emp.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={picked.has(emp.id)}
                onChange={() => toggle(emp.id)}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-800 flex-1">{emp.name}{emp.last_assigned_date ? <span className="text-[10px] text-gray-500 ml-1.5">last {fmtLastWorked(emp.last_assigned_date)}</span> : null}</span>
              {onRoute.has(emp.id) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  was sent
                </span>
              )}
              {emp.hourly_rate ? (
                <span className="text-[11px] text-gray-500">${Number(emp.hourly_rate).toFixed(0)}/h</span>
              ) : null}
            </label>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            {picked.size === 0
              ? 'Nobody selected. A stop nobody worked should be skipped, not saved with an empty crew — saving now just clears the override.'
              : picked.size === 1
                ? 'One person, so this bills one man-hour per clock hour instead of two.'
                : `${picked.size} people, so this bills ${picked.size} man-hours per clock hour.`}
          </p>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => onSave(Array.from(picked))} disabled={readOnly || (saving)} className="btn-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Change recurring frequency ────────────────────────────────────────────────
const FREQ_OPTIONS: { label: string; frequency: string; customDays?: number }[] = [
  { label: 'Every week',    frequency: 'weekly' },
  { label: 'Every 2 weeks', frequency: 'biweekly' },
  { label: 'Every 3 weeks', frequency: 'custom', customDays: 21 },
  { label: 'Every 4 weeks', frequency: 'custom', customDays: 28 },
  { label: 'Every 6 weeks', frequency: 'custom', customDays: 42 },
];

function freqLabel(frequency: string | null, customDays: number | null): string {
  if (!frequency) return '—';
  if (frequency === 'weekly') return 'Every week';
  if (frequency === 'biweekly') return 'Every 2 weeks';
  if (frequency === 'custom' && customDays) return `Every ${customDays} days`;
  return frequency;
}

function FrequencyPickerModal({ count, currentFrequency, currentCustomDays, saving, onClose, onSave }: {
  count: number;
  currentFrequency: string | null;
  currentCustomDays: number | null;
  saving: boolean;
  onClose: () => void;
  onSave: (frequency: string, customIntervalDays?: number) => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const [selected, setSelected] = React.useState<string | null>(null);
  const [customDays, setCustomDays] = React.useState<string>('');

  function handleSave() {
    if (!selected) return;
    const opt = FREQ_OPTIONS.find(o => o.label === selected);
    if (opt) {
      onSave(opt.frequency, opt.customDays);
    } else if (selected === '__custom__') {
      const days = parseInt(customDays, 10);
      if (days > 0) onSave('custom', days);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Change frequency</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {count} schedule{count !== 1 ? 's' : ''} · current: {freqLabel(currentFrequency, currentCustomDays)}
            </p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="space-y-1.5">
          {FREQ_OPTIONS.map(opt => (
            <button
              key={opt.label}
              onClick={() => setSelected(opt.label)}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium border transition-colors ${
                selected === opt.label
                  ? 'bg-brand-600 text-black border-brand-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-brand-300 hover:bg-brand-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => setSelected('__custom__')}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium border transition-colors ${
              selected === '__custom__'
                ? 'bg-brand-600 text-black border-brand-600'
                : 'bg-white text-gray-700 border-gray-200 hover:border-brand-300 hover:bg-brand-50'
            }`}
          >
            Custom interval…
          </button>
          {selected === '__custom__' && (
            <div className="flex items-center gap-2 mt-1 px-1">
              <span className="text-sm text-gray-600">Every</span>
              <input
                type="number"
                min={1}
                value={customDays}
                onChange={e => setCustomDays(e.target.value)}
                autoFocus
                className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="14"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={readOnly || (!selected || saving || (selected === '__custom__' && !parseInt(customDays, 10)))}
            className="press flex-1 py-2.5 rounded-xl bg-brand-600 text-black font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── One-tap complete ──────────────────────────────────────────────────────────
//
// Tap to finish a stop, tap again to undo. The marker matters as much as the
// checkbox: completing a job the crew never *started* has no elapsed time to
// measure, so the server copies the estimate in and flags it derived — and
// every efficiency figure in the app excludes derived rows. Faster completion,
// blurrier efficiency. So a stop finished this way says "est." out loud rather
// than passing an estimate off as a measurement.
function CompleteToggle({ job, onChanged, disabled }: {
  job: any; onChanged: () => void; disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const done = job.status === 'complete';
  const derived = !!job.actual_minutes_is_derived;

  async function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    if (busy || disabled) return;
    setBusy(true);
    try {
      if (done) {
        const { data } = await api.post(`/jobs/${job.job_id}/uncomplete`);
        toast.info(data?.message || 'Completion undone');
      } else {
        await api.post(`/jobs/${job.job_id}/complete`);
        toast.success(`${job.customer_name || 'Job'} done`);
      }
      onChanged();
    } catch (err: any) {
      // 409 means the server has a reason — an invoice, usually. Say it.
      toast.error(err?.response?.data?.message || 'Could not update that job');
    } finally {
      setBusy(false);
    }
  }

  if (disabled) {
    return <span className="h-7 w-7 flex-shrink-0" />;
  }

  return (
    <span className="flex items-center gap-1 flex-shrink-0">
      <button
        onClick={toggle}
        disabled={busy}
        title={done
          ? (derived ? 'Completed with estimated time — tap to undo' : 'Completed — tap to undo')
          : 'Mark done'}
        className={`h-7 w-7 rounded-lg border-2 flex items-center justify-center transition-colors disabled:opacity-40 ${
          done
            ? 'bg-green-600 border-green-600 text-white hover:bg-green-700'
            : 'border-gray-300 text-transparent hover:border-green-500 hover:text-green-600 hover:bg-green-50'
        }`}
      >
        <Check className="h-4 w-4" />
      </button>
      {done && derived && (
        <span title="Time was estimated, not measured — this stop does not count toward efficiency"
          className="text-[10px] text-amber-600 font-medium">est.</span>
      )}
    </span>
  );
}

// ── Inline minutes editor ─────────────────────────────────────────────────────
function InlineMinutesEdit({ jobId, currentMinutes, onSaved, disabled }: {
  jobId: number; currentMinutes: number | null; onSaved: () => void; disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [val, setVal]         = useState('');

  if (disabled) return (
    <span className="flex-shrink-0 text-xs text-gray-500">
      {currentMinutes ? `${currentMinutes}m` : '—m'}
    </span>
  );

  // ✓ carries the time to every upcoming visit AND the customer's property;
  // "1×" changes this visit only (a one-off long day).
  async function doSave(scope: 'future' | 'this' = 'future') {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/stop-pricing`, { minutes: Number(val) || 60, scope, save_to_property: scope === 'future' });
      toast.success(data?.message || 'Saved');
      setEditing(false); onSaved();
    } catch { toast.error('Failed to update minutes'); } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <input type="number" min="1" step="5" value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void doSave(); if (e.key === 'Escape') setEditing(false); }}
          className="w-12 text-xs border border-brand-300 rounded px-1 py-0.5 focus:outline-none"
          autoFocus disabled={saving} />
        <span className="text-xs text-gray-500">m</span>
        <button onMouseDown={e => { e.preventDefault(); void doSave('future'); }} disabled={saving} title="Save for this and every upcoming visit, and the customer's info"
          className="text-xs text-brand-600 font-semibold disabled:opacity-40">✓ all</button>
        <button onMouseDown={e => { e.preventDefault(); void doSave('this'); }} disabled={saving} title="This visit only"
          className="text-xs text-gray-500 font-medium disabled:opacity-40">1×</button>
        <button onMouseDown={e => { e.preventDefault(); setEditing(false); }} disabled={saving}
          className="text-xs text-gray-500 disabled:opacity-40">✕</button>
      </span>
    );
  }

  return (
    <button
      onClick={e => { e.stopPropagation(); setVal(String(currentMinutes ?? '')); setEditing(true); }}
      className={`flex-shrink-0 text-xs font-medium px-1 py-0.5 rounded transition-colors ${
        currentMinutes ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-500 hover:bg-gray-50'
      }`}>
      {currentMinutes ? `${currentMinutes}m` : '—m'}
    </button>
  );
}

// ── Inline price editor ───────────────────────────────────────────────────────
function InlinePriceEdit({ jobId, currentPrice, fallbackPrice, onSaved, disabled }: {
  jobId: number; currentPrice: number | null; fallbackPrice?: number | null; onSaved: () => void; disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [val, setVal]         = useState('');
  const displayPrice = currentPrice ?? fallbackPrice;
  const isFallback   = !currentPrice && !!fallbackPrice;

  if (disabled) return (
    <span className={`flex-shrink-0 text-xs ${currentPrice ? 'text-green-700' : isFallback ? 'text-gray-500' : 'text-amber-500'}`}>
      {currentPrice ? `$${Number(currentPrice).toFixed(0)}` : isFallback ? `~$${Number(fallbackPrice).toFixed(0)}` : '$—'}
    </span>
  );

  // ✓ carries the price to every upcoming visit AND the customer's property;
  // "1×" changes this visit only (extra work this week).
  async function doSave(scope: 'future' | 'this' = 'future') {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/stop-pricing`, { price: Number(val) || 0, scope, save_to_property: scope === 'future' });
      toast.success(data?.message || 'Saved');
      setEditing(false); onSaved();
    } catch { toast.error('Failed to update price'); } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <span className="text-xs text-gray-500">$</span>
        <input type="number" min="0" step="1" value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void doSave(); if (e.key === 'Escape') setEditing(false); }}
          className="w-14 text-xs border border-brand-300 rounded px-1 py-0.5 focus:outline-none"
          autoFocus disabled={saving} />
        <button onMouseDown={e => { e.preventDefault(); void doSave('future'); }} disabled={saving} title="Save for this and every upcoming visit, and the customer's info"
          className="text-xs text-brand-600 font-semibold disabled:opacity-40">✓ all</button>
        <button onMouseDown={e => { e.preventDefault(); void doSave('this'); }} disabled={saving} title="This visit only"
          className="text-xs text-gray-500 font-medium disabled:opacity-40">1×</button>
        <button onMouseDown={e => { e.preventDefault(); setEditing(false); }} disabled={saving}
          className="text-xs text-gray-500 disabled:opacity-40">✕</button>
      </span>
    );
  }

  return (
    <button
      onClick={e => { e.stopPropagation(); setVal(String(displayPrice ?? '')); setEditing(true); }}
      className={`flex-shrink-0 text-xs font-medium px-1 py-0.5 rounded transition-colors ${
        currentPrice ? 'text-green-700 hover:bg-green-50' :
        isFallback   ? 'text-gray-500 hover:bg-gray-50'  :
                       'text-amber-500 hover:bg-amber-50'
      }`}>
      {currentPrice   ? `$${Number(currentPrice).toFixed(0)}` :
       isFallback      ? `~$${Number(fallbackPrice).toFixed(0)}` : '$—'}
    </button>
  );
}

// ── Route stop list ───────────────────────────────────────────────────────────
/** The next weekday after a date (Sat and Sun skipped). */
function nextWeekdayAfter(iso: string): string {
  let d = addDays(iso, 1);
  while ([0, 6].includes(new Date(`${d}T12:00:00`).getDay())) d = addDays(d, 1);
  return d;
}

/**
 * The rest of a day, moved (owner, Oct 30 2026: "add a button that says move rest of routes to next day, or move rest
 * of stops, just in case it rains and we want to start things, then we can move them, slowly knock them all out").
 * The server's `remaining` move (POST /routes/:id/reschedule): only the stops not started yet, each at its own time,
 * onto whichever route runs the new day (server/services/routeForMove), finished and started stops left where they
 * are, each customer texted the new day two minutes later. Works while the crew is out — no day-in-use warning.
 * A past day offers Today; a day ahead offers Tomorrow and the next weekday; any later day can be typed.
 */
function MoveRestPanel({ routeId, date, count, onDone, onClose }: { routeId: number; date: string; count: number; onDone: () => void; onClose: () => void }) {
  const [toDate, setToDate] = useState('');
  const today = todayStr();
  const past = date < today;
  const quick: { label: string; to: string }[] = past ? [{ label: 'Today', to: today }] : [{ label: 'Tomorrow', to: addDays(date, 1) }];
  if (!past && nextWeekdayAfter(date) !== addDays(date, 1)) quick.push({ label: 'Next weekday', to: nextWeekdayAfter(date) });
  const move = useMutation(async (to: string) => (await api.post(`/routes/${routeId}/reschedule`, { from_date: date, to_date: to, scope: 'remaining' })).data, {
    onSuccess: (data: any, to: string) => {
      toast.success(<span>{data?.message || `Moved to ${fmtDateFull(to)}`}<br /><span className="text-xs">Open {fmtDateShort(to)} and tap Recalc Times if the clock matters.</span></span>, { autoClose: 12000 });
      onDone();
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not move them'); },
  });
  return (
    <div className="mb-2 rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <CalendarClock className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-xs text-amber-900">
          <p className="font-bold">Move the {count} stop{count === 1 ? '' : 's'} not started yet{past ? ` from ${fmtDateShort(date)}` : ''}</p>
          <p className="mt-0.5">Each keeps its time and lands on whichever route runs that day. Stops already done or started stay on {fmtDateShort(date)}. Customers get a text with the new day.</p>
        </div>
        <button onClick={onClose} className="press h-7 w-7 flex items-center justify-center rounded-lg text-amber-700 hover:bg-amber-100" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {quick.map(q => (
          <button key={q.to} onClick={() => move.mutate(q.to)} disabled={move.isLoading}
            className="press px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold disabled:opacity-40">
            {q.label} · {fmtDateFull(q.to)}
          </button>
        ))}
        <span className="text-xs text-amber-800">or</span>
        <input type="date" value={toDate} min={today} onChange={e => setToDate(e.target.value)}
          className="text-xs border border-amber-300 rounded-md px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-1 focus:ring-amber-400" />
        <button onClick={() => { if (toDate) move.mutate(toDate); }} disabled={move.isLoading || !toDate || toDate === date || toDate < today}
          className="press px-3 py-1.5 rounded-lg bg-white border border-amber-400 text-amber-800 text-xs font-bold disabled:opacity-40">
          {move.isLoading ? 'Moving…' : 'Move'}
        </button>
      </div>
    </div>
  );
}

/** A past day of this route with stops nobody got to (GET /routes `leftover`): one tap puts them on today. */
function LeftoverStrip({ routeId, leftover, onShow, onDone }: { routeId: number; leftover: { date: string; count: number; days?: number; total?: number }; onShow: () => void; onDone: () => void }) {
  const readOnly = useReadOnly();
  const move = useMutation(async () => (await api.post(`/routes/${routeId}/reschedule`, { from_date: leftover.date, to_date: todayStr(), scope: 'remaining' })).data, {
    onSuccess: (data: any) => { toast.success(<span>{data?.message || 'Moved to today'}<br /><span className="text-xs">Tap Recalc Times on today if the clock matters.</span></span>, { autoClose: 12000 }); onDone(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not move them'); },
  });
  const n = leftover.count;
  const more = (leftover.days || 1) > 1 ? ` (${leftover.total} over ${leftover.days} days — this is the latest)` : '';
  return (
    <div className="mx-3 sm:mx-4 my-2 flex flex-wrap items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" onClick={e => e.stopPropagation()}>
      <CalendarClock className="h-4 w-4 text-amber-700 flex-shrink-0" />
      <span className="font-semibold">{n} stop{n === 1 ? '' : 's'} from {fmtDateFull(leftover.date)} nobody got to{more}.</span>
      {!readOnly && (
        <button disabled={move.isLoading}
          onClick={() => { if (window.confirm(`Move the ${n} stop${n === 1 ? '' : 's'} from ${fmtDateFull(leftover.date)} onto today?\n\nEach keeps its time and lands on today's route; each customer is texted the new day.`)) move.mutate(); }}
          className="press px-2.5 py-1 rounded-lg bg-amber-600 text-white font-bold disabled:opacity-40">
          {move.isLoading ? 'Moving…' : 'Move them to today'}
        </button>
      )}
      <button onClick={onShow} className="underline font-semibold">Show that day</button>
    </div>
  );
}

function RouteStopList({ routeId, date, routeColor, routeIntervalDays, onRemove }: {
  routeId: number; date: string; routeColor?: string; routeIntervalDays?: number;
  onRemove: (jobId: number, scope?: string) => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const { data } = useQuery(
    ['route-stops', routeId, date],
    () => api.get(`/routes/${routeId}`, { params: { date } }).then(r => r.data)
  );
  const [localJobs, setLocalJobs]     = useState<any[]>([]);
  const [dragIdx, setDragIdx]         = useState<number | null>(null);
  const [dropIdx, setDropIdx]         = useState<number | null>(null);
  // Which stop's crew note is being written right now (job_id)
  const [noteFor, setNoteFor]         = useState<number | null>(null);
  const [viewMode, setViewMode]       = useState<'list' | 'map'>('list');
  const [addStopOpen, setAddStopOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<any | null>(null);
  const [stopBusy, setStopBusy] = useState(false);
  const [finishingRest, setFinishingRest] = useState(false);
  const [moveRestOpen, setMoveRestOpen] = useState(false);   // the "rest of the day to another day" panel
  const [rescheduleDate, setRescheduleDate] = useState<string>('');
  const [rescheduleStep, setRescheduleStep] = useState<'menu' | 'picking'>('menu');
  // "Mason didn't do these four" is one action, not four. Tick the stops, pick
  // who actually worked them.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [crewPickerOpen, setCrewPickerOpen] = useState(false);
  const [freqPickerOpen, setFreqPickerOpen] = useState(false);
  // Neighbor pairs: "Pair with…" picker for a stop, unpair via the badge
  const [pairPickFor, setPairPickFor] = useState<any | null>(null);
  const makePair = useMutation(async ({ a, b }: { a: number; b: number }) => {
    await api.post('/property-pairs', { property_a_id: a, property_b_id: b });
  }, {
    onSuccess: () => { toast.success('Paired — they\'ll always be done together'); setPairPickFor(null); qc.invalidateQueries(['route-stops', routeId]); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not pair'); },
  });
  const unpair = useMutation(async (pairId: number) => { await api.delete(`/property-pairs/${pairId}`); }, {
    onSuccess: () => { toast.info('Unpaired'); qc.invalidateQueries(['route-stops', routeId]); },
    onError: () => { toast.error('Could not unpair'); },
  });

  const recalcTimes = useMutation(async () => {
    await api.post(`/routes/${routeId}/recalculate-times`, { date: date || null });
  }, {
    onSuccess: () => {
      qc.invalidateQueries(['route-stops', routeId, date]);
      toast.success('Times recalculated');
    },
    onError: () => { toast.error('Failed to recalculate'); },
  });

  const generateDate = useMutation(async () => {
    await api.post(`/routes/${routeId}/generate-date`, { date });
  }, {
    onSuccess: (res: any) => {
      qc.invalidateQueries(['route-stops', routeId, date]);
      qc.invalidateQueries('routes');
      toast.success(res?.data?.message || 'Jobs generated');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to generate'); },
  });

  // ── Day log: Start / Pause / Resume / Finish ──────────────────────────────
  const [elapsed, setElapsed] = useState(0); // seconds, for live timer display

  const { data: logData, refetch: refetchLog } = useQuery(
    ['route-day-log', routeId, date],
    () => api.get(`/routes/${routeId}/day-log`, { params: { date } }).then(r => r.data),
    { enabled: !!date, refetchInterval: false }
  );
  const log    = logData?.log ?? null;
  const logStatus: 'not_started' | 'running' | 'paused' | 'finished' = logData?.status ?? 'not_started';

  // Live timer: tick while running
  useEffect(() => {
    if (logStatus !== 'running' || !log?.started_at) { setElapsed(0); return; }
    const startMs  = new Date(log.started_at).getTime();
    const pauseMs  = Number(log.total_pause_ms || 0);
    const tick = () => setElapsed(Math.floor((Date.now() - startMs - pauseMs) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [logStatus, log?.started_at, log?.total_pause_ms]);

  function fmtElapsed(secs: number) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }
  // Business time, never the device's zone (utils/time).
  const fmtTime = (iso: string) => fmtTsTime(iso);

  const dayLogMutate = (action: string) => api.post(`/routes/${routeId}/day-log/${action}`, { date });
  const startLog   = useMutation(() => dayLogMutate('start'),  { onSuccess: () => refetchLog(), onError: () => { toast.error('Could not start'); } });
  const pauseLog   = useMutation(() => dayLogMutate('pause'),  { onSuccess: () => refetchLog(), onError: () => { toast.error('Could not pause'); } });
  const resumeLog  = useMutation(() => dayLogMutate('resume'), { onSuccess: () => refetchLog(), onError: () => { toast.error('Could not resume'); } });
  const finishLog  = useMutation(() => dayLogMutate('finish'), { onSuccess: () => { refetchLog(); toast.success('Route finished! ✓'); }, onError: () => { toast.error('Could not finish'); } });
  // ───────────────────────────────────────────────────────────────────────────

  const apiJobs: any[] = data?.route?.jobs || [];
  const jobs       = localJobs.length > 0 ? localJobs : apiJobs;
  const isProjected = jobs.length > 0 && jobs[0]?.projected;

  useEffect(() => { setLocalJobs([]); }, [date, apiJobs.length]);

  const rescheduleJob = useMutation(async ({ jobId, newDate }: { jobId: number; newDate: string }) => {
    // The server puts the stop on the route that runs the new day and says which.
    const { data } = await api.put(`/jobs/${jobId}`, { scheduled_date: newDate });
    return data;
  }, {
    onSuccess: (data: any) => {
      qc.invalidateQueries(['route-stops', routeId, date]);
      qc.invalidateQueries('route-stops');
      qc.invalidateQueries('routes');
      toast.success(data?.message || `Rescheduled to ${fmtDateShort(rescheduleDate)}`);
      setPendingRemove(null);
      setRescheduleDate('');
      setRescheduleStep('menu');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message ? `Couldn't move it — ${e.response.data.message}` : 'Failed to reschedule'); },
  });

  // The day on screen and every later date of this route take the order
  // (owner, Sep 29 2026: "MY route order is the right one" — "that day and
  // every future week"). Sending the date re-lays that day's times, which is
  // what the crew's list reads.
  // The crew is on this day: the server kept their order and said it can be forced. The warning shows with the
  // dragged order still on screen; "Do it anyway" sends the same order with force, "Go back" drops it.
  const [inUse, setInUse] = useState<{ message: string; retry: () => void } | null>(null);
  // The page already knows the day is being run (GET /routes/:id -> stats.day_in_use), so the warning is
  // drawn the moment he drags. It used to wait on the reorder's own answer, and that call lays the new order
  // onto every later date of the route — re-timing each of them — before it replies: seconds the prompt sat
  // behind. The save still goes out alongside the warning, exactly as before — the crew's day keeps their
  // order either way, until he says "do it anyway".
  const dayInUse: string | null = data?.stats?.day_in_use
    ? (data.stats.day_in_use_message || 'This route is being run right now.')
    : null;
  const dragNo = useRef(0);     // each drag / optimize gets a number
  const answered = useRef(0);   // the last one he answered on the warning
  // Answering closes the question: a late `needs_force` from the save already in flight must not ask it again.
  const warnInUse = (message: string, retry: () => void) =>
    setInUse({ message, retry: () => { answered.current = dragNo.current; retry(); } });
  const reorder = useMutation(async ({ jobIds, force }: { jobIds: number[]; force?: boolean; no?: number }) => {
    const { data } = await api.put(`/routes/${routeId}/reorder`, { jobIds, date: date || undefined, forward: true, ...(force ? { force: true } : {}) });
    return data;
  }, {
    onSuccess: (data: any, vars) => {
      if (data?.needs_force) {
        // Not already asked and answered from the page's own flag: ask now, in the server's own words.
        if (!vars.no || vars.no > answered.current) {
          warnInUse(data.message, () => reorder.mutate({ jobIds: vars.jobIds, force: true, no: vars.no }));
        }
        return;
      }
      qc.invalidateQueries(['route-stops', routeId]);
      qc.invalidateQueries('route-stops');
      qc.invalidateQueries('routes');
      if (data?.forced || data?.carried_to?.length) toast.success(data.message, { autoClose: 6000 });
    },
    // Show what the server holds, not the drag that did not save.
    onError: () => { toast.error('Failed to reorder'); setLocalJobs([]); qc.invalidateQueries(['route-stops', routeId]); },
  });


  // Stops already on a bill can be corrected too (their pay and tips follow who worked them). When pay for one
  // already went out to someone coming off it, the server asks first (JOB_PAY_SENT).
  const setCrew = useMutation(
    async ({ jobIds, crewIds, confirmPaid }: { jobIds: number[]; crewIds: number[]; confirmPaid?: boolean }) =>
      (await api.post('/jobs/bulk-crew', { job_ids: jobIds, crew_member_ids: crewIds, ...(confirmPaid ? { confirm_paid: true } : {}) })).data,
    {
      onSuccess: (res: any) => {
        qc.invalidateQueries(['route-stops', routeId]);
        qc.invalidateQueries('profit');
        setSelected(new Set());
        setCrewPickerOpen(false);
        toast.success(res?.message || 'Crew updated');
        for (const w of res?.tip_warnings || []) toast.warn(w, { autoClose: false });
      },
      onError: (e: any, vars) => {
        const r = e?.response?.data;
        if (r?.code === 'JOB_PAY_SENT' && !vars.confirmPaid) {
          if (window.confirm(r.message)) setCrew.mutate({ ...vars, confirmPaid: true });
          return;
        }
        toast.error(r?.message || 'Failed to set crew');
      },
    }
  );

  const setFrequency = useMutation(
    async ({ scheduleIds, frequency, customIntervalDays }: { scheduleIds: number[]; frequency: string; customIntervalDays?: number }) => {
      await Promise.all(
        scheduleIds.map(id =>
          api.put(`/recurring-schedules/${id}`, { frequency, custom_interval_days: customIntervalDays ?? null })
        )
      );
    },
    {
      onSuccess: (res: any) => {
        qc.invalidateQueries(['route-stops', routeId]);
        qc.invalidateQueries(['route-stops', routeId, date]);
        setSelected(new Set());
        setFreqPickerOpen(false);
        toast.success(res?.data?.message || 'Frequency updated');
      },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to update frequency'); },
    }
  );

  function toggleSelected(jobId: number) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(jobId) ? next.delete(jobId) : next.add(jobId);
      return next;
    });
  }

  // One place for "save this order": the warning first (instant, from the page's own flag), the save alongside it.
  function saveOrder(jobIds: number[]) {
    const no = ++dragNo.current;
    if (dayInUse) warnInUse(dayInUse, () => reorder.mutate({ jobIds, force: true, no }));
    reorder.mutate({ jobIds, no });
  }

  function handleDragStart(idx: number) { setDragIdx(idx); }
  function handleDragOver(e: React.DragEvent, idx: number) { e.preventDefault(); setDropIdx(idx); }
  function handleDrop(idx: number) {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDropIdx(null); return; }
    const moved = [...jobs];
    const [removed] = moved.splice(dragIdx, 1);
    moved.splice(idx, 0, removed);
    const next = normalizePairs(moved);   // a pair moves as one block, nothing lands between them
    setLocalJobs(next);
    setDragIdx(null); setDropIdx(null);
    saveOrder(next.map((j: any) => j.job_id));
  }

  // ── Touch reorder: press-and-hold the grip on a phone, then slide ──────────
  // HTML5 drag events never fire on touch screens; this is the finger version.
  // touch-action:none on the grip keeps the page from scrolling mid-drag.
  const liRefs = useRef<(HTMLLIElement | null)[]>([]);
  const touchHold = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchFrom = useRef<number | null>(null);
  const touchOver = useRef<number | null>(null);

  function touchStart(idx: number) {
    touchFrom.current = null;
    touchHold.current = setTimeout(() => {
      touchFrom.current = idx;
      touchOver.current = idx;
      setDragIdx(idx);
      if (navigator.vibrate) navigator.vibrate(30);
    }, 300);
  }
  function touchMove(e: React.TouchEvent) {
    if (touchFrom.current === null) return;
    const y = e.touches[0].clientY;
    liRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (y >= r.top && y <= r.bottom) { touchOver.current = i; setDropIdx(i); }
    });
  }
  function touchEnd() {
    if (touchHold.current) { clearTimeout(touchHold.current); touchHold.current = null; }
    const from = touchFrom.current;
    const to = touchOver.current;
    touchFrom.current = null;
    touchOver.current = null;
    setDragIdx(null); setDropIdx(null);
    if (from !== null && to !== null && from !== to) {
      const moved = [...jobs];
      const [removed] = moved.splice(from, 1);
      moved.splice(to, 0, removed);
      const next = normalizePairs(moved);
      setLocalJobs(next);
      saveOrder(next.map((j: any) => j.job_id));
    }
  }

  // With a date picked, the server orders the day from home on real drive
  // times (Google, when the key is set) and keeps neighbour pairs together.
  // The template view has no date, so it keeps the local straight-line order.
  const serverOptimize = useMutation(({ force }: { force?: boolean }) => api.post(`/routes/${routeId}/optimize`, { date, ...(force ? { force: true } : {}) }), {
    onSuccess: (r: any) => {
      qc.invalidateQueries(['route-stops', routeId]); qc.invalidateQueries('routes');
      toast.success(r.data?.message || 'Route optimized');
    },
    onError: (e: any) => {
      if (e?.response?.status === 409 && e.response?.data?.code === 'ROUTE_IN_USE' && e.response?.data?.can_force) {
        warnInUse(e.response.data.message, () => serverOptimize.mutate({ force: true }));
      } else toast.error(e.response?.data?.message || 'Could not optimize');
    },
  });
  function handleOptimize() {
    if (date) {
      // This one the server refuses before it computes anything, so there is nothing to run alongside the
      // warning: ask now, optimize when he says so.
      if (dayInUse) { warnInUse(dayInUse, () => serverOptimize.mutate({ force: true })); return; }
      serverOptimize.mutate({});
      return;
    }
    const optimized = nearestNeighborOrder(jobs);
    setLocalJobs(optimized);
    saveOrder(optimized.map((j: any) => j.job_id));
    toast.success('Route optimized by distance');
  }

  const hasCoords     = jobs.some((j: any) => j.lat != null && j.lng != null);
  const totalEstMins  = jobs.reduce((s: number, j: any) => s + (j.estimated_minutes_snapshot || 0), 0);
  const totalDriveMins = jobs.reduce((s: number, j: any) => s + (j.drive_minutes || 0), 0);
  // A to-do riding the day (paid time, services/paidTodo) has no price and is not "missing" one.
  const missingTime   = jobs.filter((j: any) => !j.estimated_minutes_snapshot && j.service_type !== 'todo').length;
  const missingPrice  = jobs.filter((j: any) => !j.price_snapshot && j.service_type !== 'todo').length;
  const completedCount = jobs.filter((j: any) => j.status === 'complete').length;

  if (jobs.length === 0) return (
    <div>
      <p className="text-sm text-gray-500 py-2">No stops{date ? ` on ${fmtDateFull(date)}` : ''}.</p>
      <button
        onClick={() => setAddStopOpen(true)}
        className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-800 font-medium py-1 px-2 rounded hover:bg-brand-50"
      >
        <Plus className="h-3.5 w-3.5" /> Add Stop
      </button>
      {addStopOpen && (
        <AddStopModal
          routeId={routeId}
          date={date}
          defaultInterval={routeIntervalDays}
          onAdded={() => {
            setAddStopOpen(false);
            qc.invalidateQueries(['route-stops', routeId]);
            qc.invalidateQueries('routes');
          }}
          onClose={() => setAddStopOpen(false)}
        />
      )}
    </div>
  );

  return (
    <div>
      {/* ── Start / Pause / Finish bar ── */}
      {!isProjected && date && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          {logStatus === 'not_started' && (
            <button
              onClick={() => startLog.mutate()}
              disabled={readOnly || (startLog.isLoading)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              <Play className="h-4 w-4 fill-white" /> Start Route
            </button>
          )}
          {(logStatus === 'running' || logStatus === 'paused') && (
            <>
              {logStatus === 'running' ? (
                <button
                  onClick={() => pauseLog.mutate()}
                  disabled={readOnly || (pauseLog.isLoading)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yellow-500 text-white text-sm font-semibold hover:bg-yellow-600 disabled:opacity-50"
                >
                  <Pause className="h-4 w-4 fill-white" /> Pause
                </button>
              ) : (
                <button
                  onClick={() => resumeLog.mutate()}
                  disabled={readOnly || (resumeLog.isLoading)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  <Play className="h-4 w-4 fill-white" /> Resume
                </button>
              )}
              <button
                onClick={() => finishLog.mutate()}
                disabled={readOnly || (finishLog.isLoading)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                <Square className="h-4 w-4 fill-white" /> Finish Mowing
              </button>
              <span className="text-sm font-mono text-gray-600 ml-1">
                {logStatus === 'paused' ? (
                  <span className="text-yellow-600 font-semibold">⏸ Paused</span>
                ) : (
                  <span className="text-green-700">▶ {fmtElapsed(elapsed)}</span>
                )}
              </span>
            </>
          )}
          {logStatus === 'finished' && log?.started_at && (
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <Check className="h-4 w-4 text-green-600" />
              <span className="text-green-700 font-medium">Done</span>
              <span>· Started {fmtTime(log.started_at)}</span>
              {log.finished_at && <span>· Finished {fmtTime(log.finished_at)}</span>}
              {log.total_pause_ms > 0 && <span>· {fmtElapsed(Math.floor(Number(log.total_pause_ms) / 1000))} break</span>}
            </div>
          )}
        </div>
      )}

      {/* Stats bar */}
      <div className="mb-3 flex flex-wrap gap-3 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-gray-500" />
          {jobs.length} stops · {(totalEstMins / 60).toFixed(1)}h
          {totalDriveMins > 0 && <span className="text-sky-700"> · 🚗 {totalDriveMins}m driving</span>}
          {/* The card header above counts TEMPLATE positions with no date in it;
              this list counts live jobs on one date. When they disagree the page
              looked broken — "says 5, shows 3" — so say which is which and how
              to find out why. */}
          {!isProjected && data?.stats?.template_stop_count > jobs.length && (
            <span
              className="ml-1 text-amber-600"
              title={`This route has ${data.stats.template_stop_count} stops in total. `
                   + `${data.stats.template_stop_count - jobs.length} are not on this date — `
                   + `skipped, not due this week, or never generated. `
                   + `Run: node server/scripts/audit-route-date.js --route ${routeId} --date ${date}`}
            >
              ({jobs.length} of {data.stats.template_stop_count} on the route
              {data.stats.skipped_on_date > 0 ? `, ${data.stats.skipped_on_date} skipped` : ''})
            </span>
          )}
        </span>
        {completedCount > 0 && (
          <span className="flex items-center gap-1 text-green-600">
            ✓ {completedCount}/{jobs.length} done
          </span>
        )}
        {isProjected && (
          <span className="flex items-center gap-1 text-brand-600">
            <RefreshCw className="h-3 w-3" /> Projected
          </span>
        )}
        {missingTime > 0 && (
          <span className="flex items-center gap-1 text-amber-600">
            <AlertTriangle className="h-3 w-3" /> {missingTime} missing time est.
          </span>
        )}
        {missingPrice > 0 && !isProjected && (
          <span className="flex items-center gap-1 text-amber-600">
            <AlertTriangle className="h-3 w-3" /> {missingPrice} missing price
          </span>
        )}
      </div>

      {/* Financial summary (date-specific, non-projected) */}
      {!isProjected && data?.stats && (data.stats.total_revenue > 0 || data.stats.estimated_labor_cost > 0) && (
        <div className="mb-3 rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-100 text-xs space-y-1">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-emerald-700 font-medium">Rev: ${Number(data.stats.total_revenue).toFixed(0)}</span>
            {data.stats.estimated_labor_cost > 0 && (
              <span className="text-red-600">Crew: ${Number(data.stats.estimated_labor_cost).toFixed(0)}</span>
            )}
            {data.stats.estimated_labor_cost > 0 && (
              <span className={`font-semibold ${data.stats.estimated_profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                Profit: ${Number(data.stats.estimated_profit).toFixed(0)}
              </span>
            )}
          </div>
          {/* Per person, over the stops they were actually on — not the day's
              total hours times everyone's rate. The server computes it, because
              the client could not see per-stop crew and so kept showing the old
              number after a correction. */}
          {data.stats.crew_breakdown?.length > 0 && (
            <div className="flex flex-wrap gap-2 text-gray-500">
              {data.stats.crew_breakdown.map((p: any) => (
                <span key={p.employee_id}>
                  {p.name}: <span className="text-gray-700 font-medium">${Number(p.pay).toFixed(0)}</span>
                  <span className="text-gray-500 ml-0.5">
                    ({Number(p.hours).toFixed(1)}h × ${Number(p.hourly_rate || 0)}/h · {p.stops} stop{p.stops === 1 ? '' : 's'})
                  </span>
                </span>
              ))}
            </div>
          )}
          {/* The crew put the stops in a new order from the phone today (services/crewReorder): this is that order, with who and when. */}
          {data.stats.crew_reorder && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
              📱 {data.stats.crew_reorder.reason || `${data.stats.crew_reorder.who || 'The crew'} changed the order from the phone`}
              {data.stats.crew_reorder.at ? ` · ${fmtTsTime(data.stats.crew_reorder.at)}` : ''}. Today only — later weeks keep your order.
            </p>
          )}
          {/* The day does not end when the last mow does: the drive back to the yard is part of it, and it is paid
              (owner, Sep 19 2026: "add travel home on routes"). */}
          {(data.stats.home_back_minutes > 0 || data.stats.drive_from_start_minutes > 0) && (
            <p className="text-[11px] text-gray-500">
              🚗 {Number(data.stats.total_drive_minutes || 0)}m driving in all
              {data.stats.drive_between_stops_minutes != null ? ` — ${Number(data.stats.drive_between_stops_minutes)}m between stops` : ''}
              {data.stats.drive_from_start_minutes > 0 ? `, ${Number(data.stats.drive_from_start_minutes)}m out from the yard` : ''}
              {data.stats.home_back_minutes > 0 ? `, ${Number(data.stats.home_back_minutes)}m back` : ''}.
              {data.stats.back_home_at ? <> Back at the yard about <b className="text-gray-700">{(() => { const [h, m] = String(data.stats.back_home_at).split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`; })()}</b>.</> : null}
            </p>
          )}
          {data.stats.solo_stops > 0 && (
            <p className="text-[11px] text-emerald-700">
              {data.stats.solo_stops} stop{data.stats.solo_stops === 1 ? '' : 's'} worked alone —
              {' '}time stretched ×{Number(data.stats.solo_multiplier).toFixed(2)}, because the
              property estimates were timed with two people.
            </p>
          )}
        </div>
      )}

      {/* Who actually worked these stops. The route assignment says who was sent;
          this is where you say who did what when the day didn't go to plan. */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-2 flex-wrap rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
          <Users className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs font-medium text-amber-900">
            {selected.size} stop{selected.size === 1 ? '' : 's'} selected
          </span>
          <button
            onClick={() => setCrewPickerOpen(true)}
            className="text-xs font-medium px-2.5 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700"
          >
            Who worked {selected.size === 1 ? 'it' : 'these'}?
          </button>
          {jobs.some(j => selected.has(j.job_id) && j.recurring_schedule_id) && (
            <button
              onClick={() => setFreqPickerOpen(true)}
              className="press text-xs font-medium px-2.5 py-1 rounded-xl bg-brand-600 text-black hover:bg-brand-500"
            >
              Change frequency
            </button>
          )}
          <button
            onClick={() => setCrew.mutate({ jobIds: Array.from(selected), crewIds: [] })}
            disabled={readOnly || (setCrew.isLoading)}
            className="text-xs text-amber-700 hover:text-amber-900 underline disabled:opacity-40"
          >
            Reset to the route's crew
          </button>
          <button onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-amber-700 hover:text-amber-900">Clear</button>
        </div>
      )}

      {inUse && (
        <DayInUseModal message={inUse.message}
          onAnyway={() => { const r = inUse.retry; setInUse(null); r(); }}
          onBack={() => { answered.current = dragNo.current; setInUse(null); setLocalJobs([]); qc.invalidateQueries(['route-stops', routeId]); }} />
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          <button onClick={() => setViewMode('list')}
            className={`press px-3 py-1.5 flex items-center gap-1 ${viewMode === 'list' ? 'bg-brand-600 text-black' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <List className="h-3 w-3" /> List
          </button>
          <button onClick={() => setViewMode('map')}
            className={`press px-3 py-1.5 flex items-center gap-1 border-l border-gray-200 ${viewMode === 'map' ? 'bg-brand-600 text-black' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Map className="h-3 w-3" /> Map
          </button>
        </div>
        {hasCoords && jobs.length > 1 && !isProjected && (
          <button onClick={handleOptimize} disabled={reorder.isLoading}
            title={'Reorders stops by straight-line distance: starts from whichever stop is first now, '
                 + 'then repeatedly hops to the nearest one left. Not driving distance — no roads, no '
                 + 'river, no traffic. Stops without GPS go to the end. Check it before you trust it, '
                 + 'and run Recalc Times after.'}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-brand-300 text-brand-700 rounded-lg hover:bg-brand-50 disabled:opacity-50">
            <Zap className="h-3 w-3" /> Optimize
          </button>
        )}
        {!isProjected && jobs.length > 0 && (
          <button
            onClick={() => recalcTimes.mutate()}
            disabled={readOnly || (recalcTimes.isLoading)}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Timer className="h-3 w-3" /> {recalcTimes.isLoading ? 'Recalculating…' : 'Recalc Times'}
          </button>
        )}
        {isProjected && date && (
          <button
            onClick={() => generateDate.mutate()}
            disabled={readOnly || (generateDate.isLoading)}
            className="text-xs flex items-center gap-1 px-3 py-1.5 border border-brand-300 text-brand-700 rounded-lg hover:bg-brand-50 disabled:opacity-50 font-medium"
          >
            <Plus className="h-3 w-3" /> {generateDate.isLoading ? 'Generating…' : `Generate ${fmtDateShort(date)} Jobs`}
          </button>
        )}
        {/* The rest of a worked day in one tap (owner, Oct 1 2026: "make them faster"). Same /complete each
            stop's tick uses — a stop nobody started gets estimated time and stays out of efficiency. */}
        {!isProjected && date && date <= todayStr() && (() => {
          const left = jobs.filter((j: any) => j.status === 'scheduled' || j.status === 'in_progress');
          if (!left.length) return null;
          return (
            <button
              disabled={readOnly || finishingRest}
              onClick={async () => {
                if (!window.confirm(`Mark the ${left.length} stop${left.length === 1 ? '' : 's'} not done yet on ${fmtDateShort(date)} as done?\n\nStops nobody started get their estimated time (shown "est.").`)) return;
                setFinishingRest(true);
                let done = 0; const failed: string[] = [];
                for (const j of left) {
                  try { await api.post(`/jobs/${j.job_id}/complete`); done++; }
                  catch (e: any) { failed.push(`${j.customer_name || `#${j.job_id}`}: ${e?.response?.data?.message || 'failed'}`); }
                }
                setFinishingRest(false);
                qc.invalidateQueries(['route-stops', routeId]); qc.invalidateQueries('routes'); qc.invalidateQueries('attention');
                if (failed.length) toast.error(`${done} done, ${failed.length} not: ${failed.join('; ')}`, { autoClose: 12000 });
                else toast.success(`${done} stop${done === 1 ? '' : 's'} marked done`);
              }}
              className="text-xs flex items-center gap-1 px-3 py-1.5 border border-green-400 text-green-800 bg-green-50 rounded-lg font-semibold disabled:opacity-50"
            >
              <Check className="h-3 w-3" /> {finishingRest ? 'Marking…' : `Complete the rest (${left.length})`}
            </button>
          );
        })()}
        {/* Rain: the rest of the day goes to another day in one tap (owner, Oct 30 2026: "move rest of stops …
            then we can move them, slowly knock them all out"). Only stops nobody started; done ones stay. */}
        {!isProjected && date && (() => {
          const rest = jobs.filter((j: any) => j.status === 'scheduled' && !j.actual_start_time);
          if (!rest.length) return null;
          return (
            <button disabled={readOnly} onClick={() => setMoveRestOpen(o => !o)}
              title="Move the stops not started yet to another day, each at its own time, onto whichever route runs that day"
              className={`text-xs flex items-center gap-1 px-3 py-1.5 border rounded-lg font-semibold disabled:opacity-50 ${moveRestOpen ? 'border-amber-500 bg-amber-100 text-amber-900' : 'border-amber-400 bg-amber-50 text-amber-800'}`}>
              <CalendarClock className="h-3 w-3" /> Move the rest ({rest.length}) → {date < todayStr() ? 'today' : 'tomorrow'}
            </button>
          );
        })()}
        {!isProjected && (
          <button
            onClick={() => setAddStopOpen(true)}
            className="ml-auto text-xs flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
          >
            <Plus className="h-3 w-3" /> Add Stop
          </button>
        )}
      </div>

      {moveRestOpen && !isProjected && date && (
        <MoveRestPanel routeId={routeId} date={date}
          count={jobs.filter((j: any) => j.status === 'scheduled' && !j.actual_start_time).length}
          onClose={() => setMoveRestOpen(false)}
          onDone={() => {
            setMoveRestOpen(false); setLocalJobs([]);
            qc.invalidateQueries(['route-stops', routeId]); qc.invalidateQueries('route-stops'); qc.invalidateQueries('routes'); qc.invalidateQueries('calendar-jobs');
          }} />
      )}

      {viewMode === 'map' ? (
        <RouteMap jobs={jobs} color={routeColor} start={data.route?.start || null} />
      ) : (
        <ol className="space-y-1">
          {jobs.map((j: any, idx: number) => {
            const noteOpen = noteFor === j.job_id;
            return (
              <li
                key={j.id ?? idx}
                ref={el => { liRefs.current[idx] = el; }}
                draggable={!isProjected && !readOnly}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={e => handleDragOver(e, idx)}
                onDrop={() => handleDrop(idx)}
                onDragEnd={() => { setDragIdx(null); setDropIdx(null); }}
                style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
                className={`select-none flex flex-wrap items-start gap-x-2 gap-y-1 text-sm rounded-lg px-2.5 sm:px-3 py-2 transition-colors
                  ${dragIdx === idx ? 'opacity-40 bg-brand-50' : ''}
                  ${dropIdx === idx && dragIdx !== idx ? 'border-2 border-brand-400 bg-brand-50' : 'bg-gray-50 border-2 border-transparent'}
                  ${!isProjected ? 'cursor-grab active:cursor-grabbing' : ''}
                `}>
                <span
                  className="flex-shrink-0 -m-2 p-2 select-none"
                  title="Press and hold, then drag to reorder"
                  style={{ touchAction: isProjected ? 'auto' : 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                  onTouchStart={() => !isProjected && !readOnly && touchStart(idx)}
                  onTouchMove={touchMove}
                  onTouchEnd={touchEnd}
                  onTouchCancel={touchEnd}
                >
                  <GripVertical className={`h-4 w-4 mt-0.5 ${isProjected ? 'text-transparent' : 'text-gray-300'}`} />
                </span>
                {!isProjected && (
                  <input
                    type="checkbox"
                    checked={selected.has(j.job_id)}
                    onChange={() => toggleSelected(j.job_id)}
                    onClick={e => e.stopPropagation()}
                    title="Select to change who worked this stop"
                    className="mt-1 flex-shrink-0 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                )}
                <CompleteToggle
                  job={j}
                  disabled={readOnly || isProjected || j.status === 'skipped'}
                  onChanged={() => {
                    qc.invalidateQueries(['route-stops', routeId]);
                    qc.invalidateQueries('routes');
                    qc.invalidateQueries('jobs-list');
                    qc.invalidateQueries('attention');
                  }}
                />
                <span className="text-xs font-bold text-brand-600 w-5 flex-shrink-0 text-center mt-0.5">
                  {idx + 1}
                </span>
                {/* min-w keeps the name a readable column — when the badge cluster
                    doesn't fit beside it (phones), the cluster wraps below instead
                    of squeezing the name into letter-by-letter stacking. */}
                <div className="flex-1 min-w-[9.5rem]">
                  <Link
                    to={`/jobs/${j.job_id}`}
                    onClick={e => e.stopPropagation()}
                    className="block font-medium text-gray-900 leading-snug hover:text-brand-600 cursor-pointer"
                  >
                    {j.customer_name}<AccountMark mark={j.account_mark} />
                    {(j.property_label || j.address) ? ` — ${j.property_label || j.address?.split(',')[0]}` : ''}
                  </Link>
                  <p className="text-xs text-gray-500">
                    {/* A must-start time and "anytime" are his, and stay as he set them — this line used to show
                        the route's own sums in their place (a 2:00 PM must-start read "ETA 11:33 AM", an anytime
                        stop got a time), while the crew's phone showed what he had set. If the route can't reach
                        a must-start stop by its time, that is said here rather than hidden by moving the time. */}
                    {j.time_start
                      ? <span className="font-medium text-gray-700">{Number(j.time_is_firm) ? '⏰ ' : ''}{fmtWindow(j)}</span>
                      : Number(j.time_anytime) ? <span className="font-medium text-gray-700">Anytime</span> : null}
                    {Number(j.late_minutes) > 0 && j.planned_arrival && <span className="font-semibold text-red-600"> · the route gets there about {fmtClock(j.planned_arrival)}</span>}
                    {j.estimated_minutes_snapshot ? ` · ${j.estimated_minutes_snapshot}m` : ''}
                    {/* Price belongs in the readable line, not only in the right-edge
                        edit control, which reads as a button rather than data. */}
                    {j.price_snapshot ? (
                      <span className="text-green-700 font-semibold"> · ${Number(j.price_snapshot).toFixed(0)}</span>
                    ) : j.service_type === 'bonus' ? (
                      <span className="text-gray-500"> · bonus, not billed</span>
                    ) : j.service_type === 'todo' ? (
                      <span className="text-gray-500"> · to-do, paid time, not billed</span>
                    ) : j.property_mowing_price ? (
                      <span className="text-gray-500"> · ~${Number(j.property_mowing_price).toFixed(0)}</span>
                    ) : (
                      <span className="text-amber-600 font-semibold"> · no price</span>
                    )}
                    {!isProjected && j.actual_minutes ? (
                      <span className={j.actual_minutes <= (j.estimated_minutes_snapshot || Infinity) ? 'text-green-600 ml-1' : 'text-amber-600 ml-1'}>
                        {j.actual_minutes <= (j.estimated_minutes_snapshot || Infinity) ? '✓' : '⚠'} {j.actual_minutes}m actual
                      </span>
                    ) : null}
                  </p>
                  {/* Planned drive to reach this stop, plus the per-job buffer.
                      A far haul shared across a neighbour cluster says so, so a
                      single yard doesn't look like it cost the whole drive. */}
                  {j.drive_from_start_minutes > 0 && (
                    <p className="text-xs text-sky-700 mt-0.5">🚗 {j.drive_from_start_minutes}m drive from home</p>
                  )}
                  {(j.drive_minutes > 0 || j.shared_haul) && (
                    <p className="text-xs text-sky-700 mt-0.5">
                      🚗 {j.drive_minutes}m drive
                      {j.buffer_minutes ? ` · +${j.buffer_minutes}m buffer` : ''}
                      {j.shared_haul
                        ? ` · long haul split over ${j.shared_haul.over} neighbors (~${j.travel_share_minutes}m each)`
                        : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center flex-wrap justify-end gap-1 ml-auto flex-shrink-0 max-w-full" onClick={e => e.stopPropagation()}>
                {j.pair_partner_job_id ? (
                  <button disabled={readOnly}
                    title={`Neighbor pair with ${j.pair_partner_label} — always done together${j.pair_note ? ` · ${j.pair_note}` : ''}. Click to unpair.`}
                    onClick={() => { if (window.confirm(`Unpair ${j.customer_name} and ${j.pair_partner_label}?`)) unpair.mutate(j.pair_id); }}
                    className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 font-bold mt-0.5"
                  >
                    🏘 PAIR · do together
                  </button>
                ) : null}
                {j.pair_broken ? (
                  <span title="One half of this neighbor pair was skipped on this visit" className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 font-bold mt-0.5">
                    pair broken
                  </span>
                ) : null}
                {j.spray_needed ? (
                  <span title={j.spray_notes || 'Spray needed'} className="flex-shrink-0 mt-0.5">
                    <Droplets className="h-3.5 w-3.5 text-blue-500" />
                  </span>
                ) : null}
                {/* Click writes the note the CREW reads before starting. The
                    tooltip still shows whatever note already exists, including
                    the crew's own completion note and the admin-only one. */}
                <button
                  onClick={e => { e.stopPropagation(); setNoteFor(noteOpen ? null : j.job_id); }}
                  title={j.crew_notes_snapshot ? `Crew note: ${j.crew_notes_snapshot}` : (j.crew_completion_note || j.notes || 'Add a note for the crew')}
                  className={`flex-shrink-0 mt-0.5 rounded p-0.5 ${j.crew_notes_snapshot ? 'text-amber-500 hover:text-amber-600' : (j.crew_completion_note || j.notes) ? 'text-amber-400 hover:text-amber-600' : 'text-gray-300 hover:text-amber-500'}`}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </button>
                {isProjected ? (
                  <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-500 border border-brand-200 mt-0.5 whitespace-nowrap">
                    projected
                  </span>
                ) : (
                  <>
                    {j.recurring_schedule_id && (
                      <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-100 text-brand-600 flex items-center gap-0.5 mt-0.5">
                        <RefreshCw className="h-2.5 w-2.5" />
                      </span>
                    )}
                    <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full mt-0.5 ${
                      j.status === 'complete' ? 'bg-green-100 text-green-700' :
                      j.status === 'skipped'  ? 'bg-gray-100 text-gray-500' : 'bg-brand-100 text-brand-700'
                    }`}>
                      {(j.status || 'scheduled').replace('_', ' ')}
                    </span>
                  </>
                )}
                {!isProjected && j.crew_source === 'job' && (
                  <span
                    title={`Worked by ${(j.crew || []).map((c: any) => c.name).join(', ') || 'nobody'} — set on this stop, not taken from the route. Pay follows this.`}
                    className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-0.5 mt-0.5 max-w-[9rem] truncate"
                  >
                    <Users className="h-2.5 w-2.5 flex-shrink-0" />
                    {(j.crew || []).map((c: any) => String(c.name).split(' ')[0]).join(', ') || 'nobody'}
                  </span>
                )}
                <InlineMinutesEdit
                  jobId={j.job_id}
                  currentMinutes={j.estimated_minutes_snapshot || j.property_estimated_minutes}
                  onSaved={() => qc.invalidateQueries(['route-stops', routeId, date])}
                  disabled={isProjected}
                />
                <InlinePriceEdit
                  jobId={j.job_id}
                  currentPrice={j.price_snapshot}
                  fallbackPrice={j.property_mowing_price}
                  onSaved={() => qc.invalidateQueries(['route-stops', routeId, date])}
                  disabled={isProjected}
                />
                {!isProjected && !j.pair_partner_job_id && j.property_id && (
                  <button
                    title="Pair with a neighbor on this route — always done together"
                    onClick={() => setPairPickFor(j)}
                    className="p-1 text-gray-500 hover:text-indigo-600 rounded flex-shrink-0 mt-0.5 text-[11px] font-bold"
                  >
                    🏘
                  </button>
                )}
                {!isProjected && (
                  <button
                    disabled={readOnly}
                    title="Done, move, skip or remove"
                    onClick={() => { setRescheduleStep('menu'); setPendingRemove(j); }}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg flex-shrink-0">
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                )}
                </div>
                {noteOpen && (
                  <div className="basis-full w-full mt-1" onClick={e => e.stopPropagation()}>
                    <CrewNoteEditor
                      jobId={j.job_id}
                      notes={j.crew_notes_snapshot}
                      autoOpen
                      onClose={() => setNoteFor(null)}
                      onSaved={() => {
                        setNoteFor(null);
                        qc.invalidateQueries(['route-stops', routeId]);
                        qc.invalidateQueries('routes');
                        qc.invalidateQueries('jobs-list');
                      }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {pairPickFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setPairPickFor(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">🏘 Pair {pairPickFor.customer_name} with…</h3>
              <button onClick={() => setPairPickFor(null)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <p className="text-xs text-gray-500">The two stops will always sit next to each other on the route and the crew must finish both before leaving the street.</p>
            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg">
              {jobs.filter((o: any) => o.job_id !== pairPickFor.job_id && o.property_id && !o.pair_partner_job_id).map((o: any) => (
                <button key={o.job_id}
                  onClick={() => makePair.mutate({ a: pairPickFor.property_id, b: o.property_id })}
                  disabled={readOnly || (makePair.isLoading)}
                  className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 text-sm disabled:opacity-50">
                  <span className="font-medium text-gray-900">{o.customer_name}</span>
                  <span className="text-gray-500"> — {o.property_label || o.address?.split(',')[0]}</span>
                </button>
              ))}
              {jobs.filter((o: any) => o.job_id !== pairPickFor.job_id && o.property_id && !o.pair_partner_job_id).length === 0 && (
                <p className="px-3 py-3 text-sm text-gray-500">No other unpaired stops on this route today.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {crewPickerOpen && (
        <CrewPickerModal
          count={selected.size}
          routeCrew={(data?.route?.assignments || []).map((a: any) => ({ id: a.employee_id, name: a.employee_name }))}
          saving={setCrew.isLoading}
          onClose={() => setCrewPickerOpen(false)}
          onSave={crewIds => setCrew.mutate({ jobIds: Array.from(selected), crewIds })}
        />
      )}

      {freqPickerOpen && (() => {
        // Unique recurring schedule IDs for the selected stops
        const selectedRecurring = jobs.filter(j => selected.has(j.job_id) && j.recurring_schedule_id);
        const scheduleIds = Array.from(new Set(selectedRecurring.map(j => j.recurring_schedule_id as number)));
        // Current frequency — show only if all selected schedules agree
        const freqs = Array.from(new Set(selectedRecurring.map(j => j.schedule_frequency)));
        const intervalDays = Array.from(new Set(selectedRecurring.map(j => j.schedule_custom_interval_days)));
        const currentFreq = freqs.length === 1 ? freqs[0] : null;
        const currentCustom = intervalDays.length === 1 ? intervalDays[0] : null;
        return (
          <FrequencyPickerModal
            count={scheduleIds.length}
            currentFrequency={currentFreq}
            currentCustomDays={currentCustom}
            saving={setFrequency.isLoading}
            onClose={() => setFreqPickerOpen(false)}
            onSave={(frequency, customIntervalDays) =>
              setFrequency.mutate({ scheduleIds, frequency, customIntervalDays })
            }
          />
        );
      })()}


      {addStopOpen && (
        <AddStopModal
          routeId={routeId}
          date={date}
          defaultInterval={routeIntervalDays}
          onAdded={() => {
            setAddStopOpen(false);
            qc.invalidateQueries(['route-stops', routeId]);
            qc.invalidateQueries('routes');
          }}
          onClose={() => setAddStopOpen(false)}
        />
      )}

      {pendingRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {rescheduleStep === 'picking' ? 'Reschedule stop' : 'This stop'}
              </h3>
              <p className="text-sm text-gray-600">
                <span className="font-medium">{pendingRemove.customer_name}</span>
                {pendingRemove.address ? ` — ${pendingRemove.address?.split(',')[0]}` : ''}
              </p>
            </div>

            {rescheduleStep === 'picking' ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">New date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    min={todayStr()}
                    value={rescheduleDate}
                    onChange={e => setRescheduleDate(e.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Moves this job to the new date. Future recurring jobs are unaffected.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setRescheduleStep('menu'); setRescheduleDate(''); }}
                    className="flex-1 px-4 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 border border-gray-200"
                  >
                    Back
                  </button>
                  <button
                    disabled={readOnly || (!rescheduleDate || rescheduleJob.isLoading)}
                    onClick={() => rescheduleJob.mutate({ jobId: pendingRemove.job_id, newDate: rescheduleDate })}
                    className="press flex-1 px-4 py-2 rounded-xl text-sm font-medium bg-brand-600 text-black hover:bg-brand-500 disabled:opacity-40"
                  >
                    {rescheduleJob.isLoading ? 'Moving…' : 'Confirm'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {/* One sheet per stop (owner, Oct 1 2026: "make them faster"): the common moves are one tap. */}
                <div className="grid grid-cols-3 gap-2">
                  {pendingRemove.status !== 'complete' && pendingRemove.status !== 'skipped' && (
                    <button disabled={readOnly || stopBusy}
                      onClick={async () => {
                        setStopBusy(true);
                        try {
                          await api.post(`/jobs/${pendingRemove.job_id}/complete`);
                          toast.success(`${pendingRemove.customer_name || 'Stop'} done`);
                          qc.invalidateQueries(['route-stops', routeId]); qc.invalidateQueries('routes'); qc.invalidateQueries('attention');
                          setPendingRemove(null);
                        } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not complete it'); }
                        finally { setStopBusy(false); }
                      }}
                      className="px-2 py-3 rounded-lg bg-green-600 text-white text-sm font-bold disabled:opacity-40">✓ Done</button>
                  )}
                  {pendingRemove.status === 'scheduled' && [1, 2].map(n => {   // not +7: a weekly stop would land on its own next visit
                    const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() + n);
                    const to = d.toISOString().slice(0, 10);
                    return (
                      <button key={n} disabled={readOnly || rescheduleJob.isLoading}
                        onClick={() => { setRescheduleDate(to); rescheduleJob.mutate({ jobId: pendingRemove.job_id, newDate: to }); }}
                        className="px-2 py-3 rounded-lg border border-brand-300 text-brand-800 text-sm font-bold disabled:opacity-40">
                        {n === 1 ? 'Tomorrow' : 'In 2 days'}
                        <span className="block text-[11px] font-normal text-gray-600">{fmtDateShort(to)}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => { setRescheduleStep('picking'); setRescheduleDate(''); }}
                  className="w-full text-left px-4 py-3 rounded-lg border border-brand-200 hover:bg-brand-50 text-sm"
                >
                  <span className="font-medium text-brand-700 block">Reschedule to a different date</span>
                  <span className="text-xs text-gray-500">Move {fmtDateShort(date)}'s job to another day; recurring schedule continues normally</span>
                </button>
                <button
                  onClick={() => {
                    onRemove(pendingRemove.job_id, 'skip');
                    setPendingRemove(null);
                    setRescheduleStep('menu');
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                >
                  <span className="font-medium text-gray-900 block">Skip this date only</span>
                  <span className="text-xs text-gray-500">Mark {fmtDateShort(date)} as skipped; future jobs remain scheduled</span>
                </button>
                {pendingRemove.recurring_schedule_id ? (
                  <button
                    onClick={() => {
                      onRemove(pendingRemove.job_id, 'future');
                      setPendingRemove(null);
                      setRescheduleStep('menu');
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg border border-red-200 hover:bg-red-50 text-sm"
                  >
                    <span className="font-medium text-red-700 block">Remove going forward</span>
                    <span className="text-xs text-gray-500">Deactivate recurring schedule; skip all future scheduled jobs</span>
                  </button>
                ) : null}
                <button
                  onClick={() => { setPendingRemove(null); setRescheduleStep('menu'); }}
                  className="w-full px-4 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Stop modal ────────────────────────────────────────────────────────────
type AddStopStep = 'search' | 'properties' | 'create';

function AddStopModal({ routeId, date, onAdded, onClose, defaultInterval }: {
  routeId: number; date: string; onAdded: () => void; onClose: () => void; defaultInterval?: number;
}) {
  const [step, setStep]   = useState<AddStopStep>('search');
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', phone: '', address: '', price: '', minutes: '' });
  // Picking a property shows its price/time prefilled so a change carries onto
  // the stop, every generated visit, and the customer's info.
  const [pickedProp, setPickedProp] = useState<any | null>(null);
  const [stopPrice, setStopPrice] = useState('');
  const [stopMinutes, setStopMinutes] = useState('');
  const [makeRecurring, setMakeRecurring] = useState(false);
  // The template view has no date; a one-off stop still needs a day to land on.
  const [stopDate, setStopDate] = useState(date || '');
  const [recurringInterval, setRecurringInterval] = useState(defaultInterval || 14);

  const { data: searchResults, isFetching: searching } = useQuery(
    ['add-stop-customer-search', search],
    () => api.get('/customers', { params: { search } }).then(r => r.data.customers as any[]),
    { enabled: search.trim().length >= 2, keepPreviousData: true }
  );

  const { data: customerDetail } = useQuery(
    ['add-stop-customer-detail', selectedCustomer?.id],
    () => api.get(`/customers/${selectedCustomer.id}`).then(r => r.data),
    { enabled: !!selectedCustomer && step === 'properties' }
  );

  const customers: any[] = searchResults || [];
  const properties: any[] = customerDetail?.properties || [];

  // One path per case, and the job ALWAYS carries route_id — the old flow
  // created the job without it and "linked" through route_jobs only, which the
  // stop lists never read: one-time stops silently never appeared, recurring
  // ones showed up only as a generator-made duplicate.
  async function addStop(customerId: number, customerName: string, propertyId: number | null, price: number, minutes: number) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(stopDate)) { toast.error('Pick a day for this stop'); return; }
    if (makeRecurring) {
      // The schedule generator creates the stop for this date (and the future
      // ones) with route, snapshots, and position all set — no manual job.
      // The price/time go onto the property first so every visit carries them.
      const dow = new Date(stopDate + 'T12:00:00').getDay();
      const made = await api.post('/recurring-schedules', {
        customer_id: customerId,
        property_id: propertyId,
        frequency: 'custom',
        custom_interval_days: recurringInterval,
        day_of_week: dow,
        route_id: routeId,
        service_type: 'mowing',
        mowing_price: price || undefined,
        estimated_minutes: minutes || undefined,
        // Anchor the series to the date being viewed — the first visit lands
        // HERE, not on this week's same weekday.
        start_date: stopDate,
      });
      // The route was not out on the day being looked at (its off week, or a day moved for rain): say what was booked instead.
      if (made.data?.route_day?.message) toast.info(made.data.route_day.message, { autoClose: 10000 });
    } else {
      await api.post('/jobs', {
        customer_id: customerId,
        property_id: propertyId,
        scheduled_date: stopDate,
        service_type: 'mowing',
        title: 'Mowing',
        price_snapshot: price || 0,
        ...(minutes ? { estimated_minutes_snapshot: minutes } : {}),
        route_id: routeId,
        save_to_property: true,
      });
    }
    toast.success(`${customerName} added${makeRecurring ? ` — repeats ${recurringInterval === 7 ? 'every week' : recurringInterval === 14 ? 'every 2 weeks' : recurringInterval === 28 ? 'every 4 weeks' : `every ${recurringInterval} days`}` : ''}${price ? ` · $${price}` : ''}${minutes ? ` · ${minutes} min` : ''}`);
    onAdded();
  }

  function pickProperty(property: any) {
    setPickedProp(property);
    setStopPrice(property.mowing_price ? String(property.mowing_price) : '');
    setStopMinutes(property.estimated_minutes ? String(property.estimated_minutes) : '');
  }

  async function addPickedProperty() {
    if (!pickedProp) return;
    setSaving(true);
    try {
      await addStop(selectedCustomer.id, selectedCustomer.name, pickedProp.id, parseFloat(stopPrice) || 0, parseInt(stopMinutes, 10) || 0);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to add stop');
    } finally { setSaving(false); }
  }

  const cf = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCreateForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {step !== 'search' && (
              <button
                onClick={() => { setStep('search'); setSelectedCustomer(null); }}
                className="text-gray-500 hover:text-gray-600 p-0.5"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <h3 className="text-sm font-semibold text-gray-900">
              {step === 'search' && `Add Stop · ${date}`}
              {step === 'properties' && selectedCustomer?.name}
              {step === 'create' && 'New Customer'}
            </h3>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        {/* Search step */}
        {step === 'search' && (
          <>
            <div className="px-4 py-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search customer name…"
                  className="input pl-8 text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 py-1">
              {search.trim().length < 2 ? (
                <p className="text-sm text-gray-500 text-center py-8">Type a name to search</p>
              ) : searching ? (
                <p className="text-sm text-gray-500 text-center py-8">Searching…</p>
              ) : (
                <>
                  {customers.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">No customers found</p>
                  ) : (
                    <ul className="divide-y divide-gray-50">
                      {customers.map((c: any) => (
                        <li key={c.id}>
                          <button
                            onClick={() => { setSelectedCustomer(c); setStep('properties'); }}
                            className="w-full flex items-center px-4 py-2.5 hover:bg-gray-50 text-left"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{c.name}</p>
                              {c.phone && <p className="text-xs text-gray-500">{c.phone}</p>}
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="px-4 py-3 border-t border-gray-100 mt-1">
                    <button
                      onClick={() => { setCreateForm(p => ({ ...p, name: search.trim() })); setStep('create'); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 font-medium"
                    >
                      <Plus className="h-4 w-4" /> Create "{search.trim()}" as new customer
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Properties step */}
        {step === 'properties' && (
          <div className="overflow-y-auto flex-1 py-1">
            {properties.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No properties on file</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {properties.map((p: any) => (
                  <li key={p.id}>
                    <button
                      onClick={() => pickProperty(p)}
                      disabled={saving}
                      className={`w-full flex items-center px-4 py-3 text-left disabled:opacity-50 ${pickedProp?.id === p.id ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {p.label ? `${p.label} — ` : ''}{p.address}
                        </p>
                        <p className="text-xs text-gray-500">
                          {p.mowing_price ? `$${p.mowing_price}/mow` : 'No price set'}
                          {p.estimated_minutes ? ` · ~${p.estimated_minutes}m` : ' · no time set'}
                        </p>
                      </div>
                      <Plus className="h-4 w-4 text-brand-600 flex-shrink-0 ml-2" />
                    </button>
                    {pickedProp?.id === p.id && (
                      <div className="px-4 pb-3 bg-brand-50 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="label-sm">Price per mow ($)</label>
                            <input type="number" min="0" step="5" value={stopPrice} onChange={e => setStopPrice(e.target.value)} className="input text-sm py-1.5" placeholder="65" autoFocus />
                          </div>
                          <div>
                            <label className="label-sm">Time (minutes)</label>
                            <input type="number" min="0" step="5" value={stopMinutes} onChange={e => setStopMinutes(e.target.value)} className="input text-sm py-1.5" placeholder="30" />
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500">These carry onto every visit and are saved on the customer.</p>
                        <button onClick={addPickedProperty} disabled={saving} className="btn-primary w-full text-sm py-2 disabled:opacity-50">
                          {saving ? 'Adding…' : `Add to route${makeRecurring ? ` · every ${recurringInterval} days` : ' · this date only'}`}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Create customer step */}
        {step === 'create' && (
          <div className="overflow-y-auto flex-1">
            <div className="px-4 py-4">
              <CustomerIntake compact source="route" prefill={{ name: search.trim() }} submitLabel="Create & add to route"
                propertyExtras={{ mowing_price: parseFloat(createForm.price) || undefined, estimated_minutes: parseInt(createForm.minutes, 10) || undefined }}
                extraFields={(
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label-sm">Price per mow ($)</label>
                      <input type="number" min="0" step="5" value={createForm.price} onChange={cf('price')} className="input text-sm" placeholder="65" />
                    </div>
                    <div>
                      <label className="label-sm">Time (minutes)</label>
                      <input type="number" min="0" step="5" value={createForm.minutes} onChange={cf('minutes')} className="input text-sm" placeholder="30" />
                    </div>
                  </div>
                )}
                onSaved={async out => {
                  setSaving(true);
                  try {
                    await addStop(out.customer.id, out.customer.name, out.property?.id || null, parseFloat(createForm.price) || 0, parseInt(createForm.minutes, 10) || 0);
                  } catch (e: any) { toast.error(e.response?.data?.message || 'Failed to add stop'); }
                  finally { setSaving(false); }
                }} />
            </div>
          </div>
        )}

        {/* Recurring toggle — shown on properties and create steps */}
        {step !== 'search' && !date && (
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-2">
            <label htmlFor="stop-date" className="text-xs text-gray-600 flex-1">Which day?</label>
            <input id="stop-date" type="date" value={stopDate} onChange={e => setStopDate(e.target.value)}
              className="text-xs border border-gray-200 rounded px-2 py-1" />
          </div>
        )}
        {step !== 'search' && (
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-2">
            <input
              type="checkbox"
              id="make-recurring-cb"
              checked={makeRecurring}
              onChange={e => setMakeRecurring(e.target.checked)}
              className="rounded text-brand-600 cursor-pointer"
            />
            <label htmlFor="make-recurring-cb" className="text-xs text-gray-600 cursor-pointer flex-1">
              Make recurring
            </label>
            {makeRecurring && (
              <select
                value={recurringInterval}
                onChange={e => setRecurringInterval(Number(e.target.value))}
                className="text-xs border border-gray-200 rounded px-2 py-1"
              >
                <option value={7}>Weekly</option>
                <option value={14}>Biweekly</option>
                <option value={28}>Monthly (every 4 weeks)</option>
              </select>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Route form modal ──────────────────────────────────────────────────────────
function RouteFormModal({ form, setForm, onClose, onSave, saving, title }: {
  form: any; setForm: (f: any) => void;
  onClose: () => void; onSave: () => void; saving: boolean; title: string;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const ff = (k: string) => (e: React.ChangeEvent<any>) =>
    setForm((p: any) => ({ ...p, [k]: e.target.value }));
  const days = form.default_days
    ? form.default_days.split(',').map(Number).filter((n: number) => !isNaN(n))
    : [];
  function toggleDay(i: number) {
    const updated = days.includes(i) ? days.filter((d: number) => d !== i) : [...days, i];
    setForm((p: any) => ({ ...p, default_days: updated.sort((a: number, b: number) => a - b).join(',') }));
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="label-sm">Route Name *</label>
            <input value={form.name} onChange={ff('name')} className="input" placeholder="e.g. Monday Weekly" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-sm">Category</label>
              <select value={form.route_category} onChange={ff('route_category')} className="input">
                <option value="mowing">Mowing</option>
                <option value="fertilizing">Fertilizing</option>
              </select>
            </div>
            <div>
              <label className="label-sm">Route Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={ff('color')}
                  className="h-9 w-14 rounded border border-gray-300 cursor-pointer" />
                <div className="flex gap-1 flex-wrap">
                  {ROUTE_COLORS.slice(0, 5).map(c => (
                    <button key={c} type="button"
                      onClick={() => setForm((p: any) => ({ ...p, color: c }))}
                      className="w-5 h-5 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div>
            <label className="label-sm">Runs on days</label>
            <div className="flex gap-1.5 flex-wrap">
              {DAY_NAMES.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDay(i)}
                  className={`px-2.5 py-1 text-xs rounded-full border font-medium transition-colors ${
                    days.includes(i)
                      ? 'bg-brand-600 text-black border-brand-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-brand-400'
                  }`}>{d}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-sm">Start Time</label>
            <div className="flex gap-2">
              <input type="time" value={form.planned_start_time} onChange={ff('planned_start_time')}
                className="input flex-1" placeholder="08:00" />
              {form.planned_start_time && (
                <button type="button" onClick={() => setForm((p: any) => ({ ...p, planned_start_time: '' }))} className="text-xs px-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50" title="No set start time">Clear</button>
              )}
            </div>
            <label className="mt-1.5 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={!!form.leaves_home}
                onChange={e => setForm((p: any) => ({ ...p, leaves_home: e.target.checked }))} className="rounded text-brand-600" />
              Start time is when we leave home — the drive to the first yard is paid and goes on the clock
            </label>
          </div>
          <div>
            <label className="label-sm">Start location <span className="text-gray-500 font-normal">(for the crew's map)</span></label>
            <input type="text" value={form.start_address} onChange={ff('start_address')} className="input"
              placeholder="Blank = home base" />
            <p className="mt-1 text-xs text-gray-500">
              The crew's route map draws a Start pin here and the route line begins at it. Leave it blank for home base.
              {form._saved_start?.address && !form._saved_start.pinned && form.start_address === form._saved_start.address && (
                <span className="block text-amber-700 mt-0.5">The map could not find “{form._saved_start.address}” — the pin is home base until it does. Try the full street address with the town.</span>
              )}
            </p>
          </div>
          <div>
            <label className="label-sm">Notes</label>
            <textarea value={form.notes} onChange={ff('notes')} className="input" rows={2} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!form.is_active}
              onChange={e => setForm((p: any) => ({ ...p, is_active: e.target.checked }))}
              className="rounded text-brand-600" />
            Active route
          </label>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={onSave} disabled={readOnly || (saving)} className="btn-primary">
            {saving ? 'Saving…' : 'Save Route'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Assignment panel ──────────────────────────────────────────────────────────
// Owner, Oct 1 2026 ("make them faster"): the crew is usually the same as last
// run, so that is one tap; picking people is tapping names, not a dropdown per
// worker; and "every later run with nobody on it" carries it forward.
function AssignmentPanel({ routeId, selectedDate, employees, onSaved }: {
  routeId: number; selectedDate: string; employees: any[]; onSaved: () => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [picked, setPicked]   = useState<number[]>([]);   // first one picked leads
  const [forward, setForward] = useState(false);

  const { data: assignData } = useQuery(
    ['route-assignment', routeId, selectedDate],
    () => api.get(`/routes/${routeId}`, { params: { date: selectedDate } })
      .then(r => (r.data.route?.assignments || []) as any[]),
    { enabled: !!selectedDate }
  );
  const initialAssignments = assignData || [];
  const { data: lastCrew } = useQuery(
    ['route-last-crew', routeId, selectedDate],
    () => api.get(`/routes/${routeId}/last-crew`, { params: { before: selectedDate } }).then(r => r.data as { day: string | null; crew: any[] }),
    { enabled: !!selectedDate && !readOnly }
  );

  useEffect(() => { setEditing(false); }, [selectedDate]);

  function startEdit() {
    const ordered = [...initialAssignments].sort((a: any, b: any) => (a.route_role === 'leader' ? -1 : 0) - (b.route_role === 'leader' ? -1 : 0));
    setPicked(ordered.map((a: any) => Number(a.employee_id)));
    setForward(false);
    setEditing(true);
  }
  const flip = (id: number) => setPicked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const save = useMutation(async (body: { ids: number[]; forward: boolean }) => {
    const payload = body.ids.map((id, i) => ({ employee_id: id, route_role: i === 0 ? 'leader' : 'member' }));
    return api.put(`/routes/${routeId}/assignments`, { date: selectedDate, employees: payload, forward: body.forward }).then(r => r.data);
  }, {
    onSuccess: (d: any) => {
      setEditing(false);
      qc.invalidateQueries(['route-assignment', routeId]);
      onSaved();
      toast.success(d?.forward_days ? `Crew set — and on ${d.forward_days} later run${d.forward_days === 1 ? '' : 's'}` : 'Crew assigned');
      // A tip some of which already went to someone this change took off a visit (services/tipShare).
      for (const w of d?.tip_warnings || []) toast.warn(w, { autoClose: false });
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  const leader  = initialAssignments.find((a: any) => a.route_role === 'leader');
  const members = initialAssignments.filter((a: any) => a.route_role === 'member');
  const sameAsLast = lastCrew?.day && lastCrew.crew.length > 0 ? lastCrew : null;

  if (!editing) {
    return (
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 mb-0.5">Crew · {fmtDateFull(selectedDate)}</p>
          {initialAssignments.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No crew assigned</p>
          ) : (
            <p className="text-xs text-gray-700">
              {leader && <span className="font-medium">{leader.employee_name}</span>}
              {members.length > 0 && (
                <span className="text-gray-500">
                  {leader ? ' · ' : ''}{members.map((m: any) => m.employee_name.split(' ')[0]).join(', ')}
                </span>
              )}
            </p>
          )}
        </div>
        {initialAssignments.length === 0 && sameAsLast && (
          <button disabled={readOnly || save.isLoading}
            onClick={() => save.mutate({ ids: sameAsLast.crew.map((c: any) => Number(c.employee_id)), forward: false })}
            className="text-xs font-bold text-white bg-brand-600 rounded px-2 py-1.5 flex-shrink-0">
            Same as {fmtDateShort(sameAsLast.day!)}: {sameAsLast.crew.map((c: any) => String(c.employee_name).split(' ')[0]).join(', ')}
          </button>
        )}
        <button onClick={startEdit}
          className="text-xs font-medium text-brand-600 hover:text-brand-800 border border-brand-200 rounded px-2 py-1 flex-shrink-0 bg-white">
          {initialAssignments.length === 0 ? 'Pick people' : 'Change who is on it'}
        </button>
      </div>
    );
  }

  const people = [...employees].sort(byRecentAssignment);
  return (
    <div className="px-4 py-3 border-b border-gray-100 bg-brand-50 space-y-3">
      <p className="text-xs font-semibold text-gray-600">Who is on it · {fmtDateFull(selectedDate)} <span className="font-normal">— tap names; the first one leads</span></p>
      <div className="flex flex-wrap gap-2">
        {people.map((e: any) => {
          const at = picked.indexOf(Number(e.id));
          return (
            <button key={e.id} onClick={() => flip(Number(e.id))}
              className={`px-3 py-2 rounded-full border text-sm font-semibold ${at >= 0 ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>
              {at === 0 ? '★ ' : ''}{e.name}
            </button>
          );
        })}
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" checked={forward} onChange={e => setForward(e.target.checked)} className="h-4 w-4" />
        Also every later run of this route that has nobody on it yet
      </label>
      <div className="flex gap-2">
        <button onClick={() => save.mutate({ ids: picked, forward })} disabled={readOnly || save.isLoading}
          className="btn-primary text-sm py-1.5 px-4">
          {save.isLoading ? 'Saving…' : 'Save Crew'}
        </button>
        <button onClick={() => setEditing(false)} className="btn-secondary text-sm py-1.5 px-4">Cancel</button>
      </div>
    </div>
  );
}

/**
 * Two routes on one day.
 *
 * The owner, Sep 21 2026: "Tuesday's got two September 22nd routes… there's
 * some people I don't think are supposed to be on it," and then: "figure that
 * out." A fortnightly route shows up on its own OFF week carrying a couple of
 * stops, beside the round that really is running.
 *
 * Two different faults look identical on the page, so this never guesses: a
 * yard mowed every week stands on the route's own days too, and only the list
 * it hangs off is wrong — one tap fixes that and no date moves. A yard that
 * only ever appears on the off days has the wrong DATE, which changes when
 * somebody gets mowed, so that one is his call and is only ever reported here.
 */
function OddDays() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [look, setLook] = useState<any>(null);
  const [keepTomorrow, setKeepTomorrow] = useState(true);
  const { data } = useQuery('route-odd-days', () => api.get('/routes/odd-days').then(r => r.data), { staleTime: 60_000 });
  const routes: any[] = (data?.routes || []).filter((r: any) => (r.stops || []).length > 0);

  const tomorrow = (() => { const d = new Date(`${businessToday()}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); })();
  const tidy = useMutation(
    (dryRun: boolean) => api.post('/routes/odd-days/join-running-route',
      { dry_run: dryRun, ...(keepTomorrow ? { from: tomorrow } : {}) }).then(r => r.data),
    {
      onSuccess: (d: any) => {
        setLook(d);
        if (!d.dry_run) {
          toast.success(d?.message || 'Tidied', { autoClose: 9000 });
          qc.invalidateQueries('route-odd-days'); qc.invalidateQueries('routes');
        }
      },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not tidy those'); },
    });

  if (!routes.length) return null;
  const weekly = routes.reduce((t, r) => t + (r.stops || []).filter((s: any) => s.also_on_the_round).length, 0);
  const wrongWeek = routes.reduce((t, r) => t + (r.stops || []).filter((s: any) => s.schedule_id && !s.also_on_the_round).length, 0);

  return (
    <div className="card p-4 border border-amber-300 bg-amber-50/60 space-y-2.5">
      <p className="text-sm font-semibold text-amber-900">Two routes on one day</p>
      {routes.map((r: any) => (
        <div key={r.route_id} className="text-xs text-amber-900/90">
          <p><b>{r.route}</b> runs every {r.rhythm} days — {r.own_days.join(' · ')}</p>
          <p className="text-amber-800">It is also booked on {r.odd_days.join(' · ')}, which is why two show up on one date.</p>
          <ul className="mt-1 space-y-0.5">
            {(r.stops || []).map((s: any) => (
              <li key={s.job_id}>
                <b>{s.customer}</b> · {s.date}
                <span className={s.also_on_the_round ? 'text-emerald-800' : 'text-amber-700'}> — {s.why}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {look && (
        <div className="rounded-lg bg-white border border-amber-200 p-2.5 text-xs text-gray-800 space-y-0.5">
          <p className="font-semibold">{look.dry_run ? 'This is what it would do:' : look.message}</p>
          {(look.moved || []).map((m: any, i: number) => <p key={i}>{m.date} — {m.customer}: {m.from} → {m.to}</p>)}
          {!(look.moved || []).length && <p className="text-gray-500">Nothing to move with these settings.</p>}
        </div>
      )}

      <label className="flex items-center gap-2 text-xs text-amber-900">
        <input type="checkbox" checked={keepTomorrow} onChange={e => { setKeepTomorrow(e.target.checked); setLook(null); }} />
        Leave tomorrow exactly as it is — the crew already has that list
      </label>

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => tidy.mutate(true)} disabled={readOnly || (tidy.isLoading || !weekly)}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-amber-700 text-white font-semibold disabled:opacity-50">
          {tidy.isLoading ? 'Working…' : 'Look first'}
        </button>
        {look?.dry_run && (look.moved || []).length > 0 && (
          <button type="button" onClick={() => tidy.mutate(false)} disabled={readOnly || (tidy.isLoading)}
            className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
            Put those {look.moved.length} on the right list
          </button>
        )}
        <span className="text-[11px] text-amber-900/70">
          {weekly} mowed every week — moving their list changes nothing for them.
          {wrongWeek ? ` ${wrongWeek} on the wrong week: that changes their mowing day, so tell me before I touch those.` : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * A day written out somewhere else, put in as a route.
 *
 * The owner, Sep 22 2026, pasting a planned afternoon: "no put that in the app
 * as is as a route", and "assigned to Michael". He plans days outside the app;
 * this saves retyping seven stops and their times.
 *
 * It always looks first. A wrong match books a stranger at somebody's door, so
 * every stop says who it matched and why before anything is written.
 */
function PasteRoute() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [date, setDate] = useState(businessToday());
  const [name, setName] = useState('');
  const [look, setLook] = useState<any>(null);

  const run = useMutation(
    (dryRun: boolean) => api.post('/routes/paste', {
      text, date, name: name.trim() || undefined, dry_run: dryRun,
    }).then(r => r.data),
    {
      onSuccess: (d: any) => {
        setLook(d);
        if (!d.dry_run) {
          toast.success(d?.message || 'Route made', { autoClose: 9000 });
          qc.invalidateQueries('routes'); qc.invalidateQueries('route-odd-days');
          setText('');
        }
      },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not put that day in'); },
    });

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="text-xs font-semibold text-brand-700 underline underline-offset-2">
        Paste a day in as a route
      </button>
    );
  }

  return (
    <div className="card p-4 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">Paste a day in as a route</p>
        <button type="button" onClick={() => { setOpen(false); setLook(null); }} className="text-xs text-gray-500">Close</button>
      </div>
      <p className="text-[11px] text-gray-500">
        Paste the table as it was written — number, stop, drive, on site. Home top and tail becomes the leave and the
        return. It matches on the address first, then the name, and shows you every match before booking anything.
      </p>
      <textarea value={text} onChange={e => { setText(e.target.value); setLook(null); }} rows={7}
        placeholder={'#,Stop,Drive,On site\n—,445 Monahans,—,leave 2:00\n1,"Chris Land — 3921 Venezia View, Leander",15 min,2:15–2:30'}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs font-mono" />
      <div className="flex items-end gap-2 flex-wrap">
        <label className="text-[11px] text-gray-600">The day it runs
          <input type="date" value={date} onChange={e => { setDate(e.target.value); setLook(null); }} className="input mt-0.5 py-1.5 text-xs" /></label>
        <label className="text-[11px] text-gray-600 flex-1 min-w-[10rem]">Call it <span className="text-gray-400">(optional)</span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Quote visits — Tuesday"
            className="input mt-0.5 py-1.5 text-xs" /></label>
      </div>

      {look && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5 text-xs text-gray-800 space-y-1">
          <p className="font-semibold">{look.message}</p>
          {(look.stops || []).map((st: any) => (
            <p key={st.position} className={st.property_id ? '' : 'text-amber-700'}>
              {st.position}. {st.start}–{st.end} · <b>{st.name || st.address}</b>
              {st.matched_to ? <span className="text-gray-500"> → {st.matched_to} ({st.why})</span>
                : <span> — {st.why}</span>}
            </p>
          ))}
          {(look.made || []).map((m: any) => <p key={m.job_id} className="text-emerald-800">{m.position}. {m.at} · {m.customer}</p>)}
          {(look.skipped || []).map((sk: any, i: number) => <p key={i} className="text-amber-700">Left out: {sk.stop} — {sk.why}</p>)}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => run.mutate(true)} disabled={readOnly || (run.isLoading || !text.trim())}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-800 text-white font-semibold disabled:opacity-50">
          {run.isLoading ? 'Reading…' : 'Look first'}
        </button>
        {look?.dry_run && look.matched > 0 && (
          <button type="button" onClick={() => run.mutate(false)} disabled={readOnly || (run.isLoading)}
            className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
            Make the route — {look.matched} stop{look.matched === 1 ? '' : 's'}
          </button>
        )}
        <span className="text-[11px] text-gray-500">Assigned to you. Nothing is texted or charged.</span>
      </div>
    </div>
  );
}
